import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { ChevronRight, ArrowLeft, Lock, Database, Eye, X, ArrowUpDown, ArrowUp, ArrowDown, Calendar, Info, Plus, Minus, Pencil, AlertTriangle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MetricTooltip } from '../components/MetricTooltip';
import AvailabilityPill from '../components/AvailabilityPill';
import { classifyDates, classifyRowsByKey, keepDateWiseRows, buildChartCaption } from '../lib/availability';
import { useMetadataIcons } from '../lib/useMetadataIcons';
import MonthCompare, { colorForMonth, dashForMonth, formatMonthChip } from '../components/MonthCompare';
import { AD_PREVIEWS, getAdPreview } from '../lib/adPreviewsSpec';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANNELS = ['Flipkart', 'Amazon', 'D2C', 'Blinkit', 'Zepto'];

// Inline-editable Revenue cell for Off-Platform Ads. Click to enter edit mode;
// Enter/blur saves, Esc cancels. Hovering shows a pencil + (when overridden) clear button.
function EditableRevenue({ value, isOverridden, onSave, onClear }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const fmt = (v) => `Rs. ${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  if (editing) {
    return (
      <input
        type="number"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { onSave(parseFloat(draft) || 0); setEditing(false); }
          else if (e.key === 'Escape') setEditing(false);
        }}
        onBlur={() => { onSave(parseFloat(draft) || 0); setEditing(false); }}
        className="w-full text-right px-2 py-1 border border-[#37b6ad] rounded text-sm tabular-nums focus:outline-none"
        data-testid="editable-revenue-input"
      />
    );
  }
  return (
    <span
      onClick={() => { setDraft(String(value || 0)); setEditing(true); }}
      className={`group inline-flex items-center justify-end gap-1.5 cursor-pointer rounded px-1 py-0.5 hover:bg-amber-50/60 ${isOverridden ? 'text-[#0F0F0F] font-medium' : 'text-gray-500'}`}
      title={isOverridden ? 'Manually entered — click to edit' : 'Click to enter manually'}
      data-testid="editable-revenue-cell"
    >
      <span>{fmt(value)}</span>
      <Pencil className="w-3 h-3 text-gray-300 group-hover:text-[#37b6ad] transition-colors" />
      {isOverridden && onClear && (
        <button
          onClick={(e) => { e.stopPropagation(); onClear(); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          title="Clear override"
          data-testid="editable-revenue-clear"
        >
          <X className="w-3 h-3 text-rose-400 hover:text-rose-600" />
        </button>
      )}
    </span>
  );
}

const AD_CHANNEL_MAP = {
  'Flipkart': {
    'Flipkart Ads': ['PLA', 'PCA', 'Brand Ads'],
    'Off-Platform Ads': ['Telegram Ads', 'Whatsapp Ads', 'Google Ads', 'Meta Ads', 'Meta Ads by FK', 'Criteo by FK']
  },
  'Amazon': {
    'Amazon Ads': ['Sponsored Products', 'Sponsored Brand', 'Sponsored Display', 'ONO DSP'],
    'Off-Platform Ads': ['3P DSP', 'Telegram Ads']
  },
  'Zepto': {
    'Zepto Ads': ['Sponsored Products', 'Sponsored Brand', 'Sponsored Display']
  },
  'Blinkit': {
    // Blinkit's marketplace ads sit under a single "On-Platform Ads" umbrella.
    // The user requested a 3-level drill: Channel → Performance / Reach → product.
    // We model Performance & Reach as standalone ad_types here (each with its
    // own product list) and the rendering layer below shows a wrapping
    // "On-Platform Ads" summary table that drills into either one via
    // `drillBlinkitAdType`. Off-platform Blinkit ads can be added as a
    // sibling ad_type later if needed.
    'Performance': ['Product Booster', 'Recommendation Ads', 'Product Shelf'],
    'Reach':       ['Stories', 'Prime Banner', 'Brand Spotlight', 'Listing Spotlight', 'Brand Booster'],
  },
  'D2C': {
    'Meta Ads':   ['Instagram', 'Facebook', 'Threads'],
    'Google Ads': ['YouTube', 'Search Network'],
    // "WhatsApp" here = legacy Meta-CSV-sourced WhatsApp rows (Facebook Ads
    // Manager platform=whatsapp). "Automation" / "Manual" are the dedicated
    // D2C WhatsApp exports with their own wide-monthly parser. Kept side by
    // side so historic data stays visible while new uploads route separately.
    'WhatsApp':   ['WhatsApp', 'Automation', 'Manual'],
  },
};

// Ad products that only have data available from May 2026 onwards. Rows are
// hidden from the Off-Platform Ads table when the selected date range ends
// before 2026-05-01, and the totals row recomputes accordingly.
const POST_MAY_2026_PRODUCTS = new Set(['Meta Ads by FK', 'Criteo by FK']);

// Campaign-level column definitions per ad_product
const CAMPAIGN_COLS = {
  PLA: [
    { key: 'campaign_name', label: 'Campaign name', align: 'left' },
    { key: 'ad_spend', label: 'Ad spend', fmt: 'currency' },
    { key: 'views', label: 'Views', fmt: 'number' },
    { key: 'clicks', label: 'Clicks', fmt: 'number' },
    { key: 'ctr', label: 'Ctr', fmt: 'pct' },
    { key: 'total_converted_units', label: 'Total converted units', fmt: 'number' },
    { key: 'cvr', label: 'Cvr', fmt: 'pct' },
    { key: 'total_revenue', label: 'Total revenue', fmt: 'currency' },
    { key: 'roi', label: 'Roi', fmt: 'decimal' },
    { key: 'acos', label: 'Acos', fmt: 'pct' },
  ],
  PCA: [
    { key: 'campaign_name', label: 'Campaign name', align: 'left' },
    { key: 'ad_group', label: 'Ad group', align: 'left' },
    { key: 'ad_spend', label: 'Ad spend', fmt: 'currency' },
    { key: 'views', label: 'Views', fmt: 'number' },
    { key: 'clicks', label: 'Clicks', fmt: 'number' },
    { key: 'ctr', label: 'Ctr', fmt: 'pct' },
    { key: 'total_converted_units', label: 'Total converted units', fmt: 'number' },
    { key: 'cvr', label: 'Cvr', fmt: 'pct' },
    { key: 'total_revenue', label: 'Total revenue (Rs.)', fmt: 'currency' },
    { key: 'roi', label: 'Roi', fmt: 'decimal' },
    { key: 'acos', label: 'Acos', fmt: 'pct' },
  ],
  'Brand Ads': [
    { key: 'campaign_name', label: 'Campaign name', align: 'left' },
    { key: 'ad_spend', label: 'Ad spend', fmt: 'currency' },
    { key: 'views', label: 'Views', fmt: 'number' },
    { key: 'clicks', label: 'Clicks', fmt: 'number' },
    { key: 'ctr', label: 'Ctr', fmt: 'pct' },
    { key: 'converted_units_view', label: 'Converted units (view)', fmt: 'number' },
    { key: 'converted_units_clicks', label: 'Converted units (clicks)', fmt: 'number' },
    { key: 'total_converted_units', label: 'Total converted units', fmt: 'number' },
    { key: 'cvr', label: 'Cvr', fmt: 'pct' },
    { key: 'revenue_units_view', label: 'Revenue units (view)', fmt: 'currency' },
    { key: 'revenue_units_clicks', label: 'Revenue units (clicks)', fmt: 'currency' },
    { key: 'total_revenue', label: 'Total revenue units', fmt: 'currency' },
    { key: 'roi', label: 'Roi', fmt: 'decimal' },
    { key: 'acos', label: 'Acos', fmt: 'pct' },
  ],
  'Sponsored Products': [
    { key: 'campaign_name', label: 'Campaign Name', align: 'left' },
    { key: 'Portfolio name', label: 'Portfolio', align: 'left' },
    { key: 'Campaign Type', label: 'Campaign Type', align: 'left' },
    { key: 'Targeting Type', label: 'Targeting', align: 'left' },
    { key: 'Bidding strategy', label: 'Bidding Strategy', align: 'left' },
    { key: 'Status', label: 'Status', align: 'left' },
    { key: 'Budget', label: 'Budget', fmt: 'currency_raw' },
    { key: 'Impressions', label: 'Impressions', fmt: 'number' },
    { key: 'Last Year Impressions', label: 'LY Impressions', fmt: 'number' },
    { key: 'Clicks', label: 'Clicks', fmt: 'number' },
    { key: 'Last Year Clicks', label: 'LY Clicks', fmt: 'number' },
    { key: 'Click-Through Rate (CTR)', label: 'CTR', fmt: 'pct_raw' },
    { key: 'ad_spend', label: 'Spend', fmt: 'currency' },
    { key: 'ad_spend_gst', label: 'Spend (Incl. GST)', fmt: 'currency', computed: true },
    { key: 'Last Year Spend', label: 'LY Spend', fmt: 'currency_raw' },
    { key: 'Cost Per Click (CPC)', label: 'CPC', fmt: 'currency_raw' },
    { key: 'Last Year Cost Per Click (CPC)', label: 'LY CPC', fmt: 'currency_raw' },
    { key: '14 Day Total Orders (#)', label: 'Orders (14d)', fmt: 'number' },
    { key: '14 Day Total Sales (₹)', label: 'Sales (14d)', fmt: 'currency_raw' },
    { key: 'Total Advertising Cost of Sales (ACOS) ', label: 'ACOS', fmt: 'pct_raw' },
    { key: 'Total Return on Advertising Spend (ROAS)', label: 'ROAS', fmt: 'decimal_raw' },
  ],
  'Sponsored Brand': [
    { key: 'campaign_name', label: 'Campaign Name', align: 'left' },
    { key: 'Portfolio name', label: 'Portfolio', align: 'left' },
    { key: 'Cost Type', label: 'Cost Type', align: 'left' },
    { key: 'Impressions', label: 'Impressions', fmt: 'number' },
    { key: 'Viewable Impressions', label: 'Viewable Impr.', fmt: 'number' },
    { key: 'Clicks', label: 'Clicks', fmt: 'number' },
    { key: 'Click-Through Rate (CTR)', label: 'CTR', fmt: 'pct_raw' },
    { key: 'Cost Per Click (CPC)', label: 'CPC', fmt: 'currency_raw' },
    { key: 'Cost per 1,000 viewable impressions (VCPM)', label: 'vCPM', fmt: 'currency_raw' },
    { key: 'View-Through Rate (VTR)', label: 'VTR', fmt: 'pct_raw' },
    { key: 'Click-Through Rate for Views (vCTR)', label: 'vCTR', fmt: 'pct_raw' },
    { key: 'ad_spend', label: 'Spend', fmt: 'currency' },
    { key: 'ad_spend_gst', label: 'Spend (Incl. GST)', fmt: 'currency', computed: true },
    { key: 'Total Advertising Cost of Sales (ACOS) ', label: 'ACOS', fmt: 'pct_raw' },
    { key: 'Total Return on Advertising Spend (ROAS)', label: 'ROAS', fmt: 'decimal_raw' },
    { key: '14 Day Total Sales (₹)', label: 'Sales 14d', fmt: 'currency_raw' },
    { key: '14 Day Total Orders (#)', label: 'Orders 14d', fmt: 'number' },
    { key: '14 Day Total Units (#)', label: 'Units 14d', fmt: 'number' },
    { key: '14 Day Conversion Rate', label: 'CVR 14d', fmt: 'pct_raw' },
    { key: 'Video First Quartile Views', label: 'Video Q1', fmt: 'number' },
    { key: 'Video Midpoint Views', label: 'Video Mid', fmt: 'number' },
    { key: 'Video Third Quartile Views', label: 'Video Q3', fmt: 'number' },
    { key: 'Video Complete Views', label: 'Video Complete', fmt: 'number' },
    { key: 'Video Unmutes', label: 'Video Unmutes', fmt: 'number' },
    { key: '5 Second Views', label: '5s Views', fmt: 'number' },
    { key: '5 Second View Rate', label: '5s View Rate', fmt: 'pct_raw' },
    { key: '14 Day Branded Searches', label: 'Branded Searches', fmt: 'number' },
    { key: '14 Day Detail Page Views (DPV)', label: 'DPV 14d', fmt: 'number' },
    { key: '14 Day New-to-brand Orders (#)', label: 'NTB Orders', fmt: 'number' },
    { key: '14 Day % of Orders New-to-brand', label: '% NTB Orders', fmt: 'pct_raw' },
    { key: '14 Day New-to-brand Sales (₹)', label: 'NTB Sales', fmt: 'currency_raw' },
    { key: '14 Day % of Sales New-to-brand', label: '% NTB Sales', fmt: 'pct_raw' },
    { key: '14 Day New-to-brand Units (#)', label: 'NTB Units', fmt: 'number' },
    { key: '14 Day % of Units New-to-brand', label: '% NTB Units', fmt: 'pct_raw' },
    { key: '14 Day New-to-brand Order Rate', label: 'NTB Order Rate', fmt: 'pct_raw' },
    { key: 'Total Advertising Cost of Sales (ACOS) – (Click)', label: 'ACOS (Click)', fmt: 'pct_raw' },
    { key: 'Total Return on Advertising Spend (ROAS) – (Click)', label: 'ROAS (Click)', fmt: 'decimal_raw' },
    { key: '14 Day Total Sales – (Click)', label: 'Sales 14d (Click)', fmt: 'currency_raw' },
    { key: '14 Day Total Orders (#) – (Click)', label: 'Orders 14d (Click)', fmt: 'number' },
    { key: '14 Day Total Units (#) – (Click)', label: 'Units 14d (Click)', fmt: 'number' },
    { key: 'New-to-brand detail page views', label: 'NTB DPV', fmt: 'number' },
    { key: 'New-to-brand detail page view click-through conversions', label: 'NTB DPV CTC', fmt: 'number' },
    { key: 'New-to-brand detail page view rate', label: 'NTB DPV Rate', fmt: 'pct_raw' },
    { key: 'Effective cost per new-to-brand detail page view', label: 'eCPNTB-DPV', fmt: 'currency_raw' },
    { key: 'Brand Store page views', label: 'Brand Store Views', fmt: 'number' },
    { key: '14 Day ATC', label: 'ATC 14d', fmt: 'number' },
    { key: '14 Day ATC Clicks', label: 'ATC Clicks 14d', fmt: 'number' },
    { key: '14 Day ATCR', label: 'ATCR 14d', fmt: 'pct_raw' },
    { key: 'Effective cost per Add to Cart (eCPATC)', label: 'eCPATC', fmt: 'currency_raw' },
    { key: 'Branded Searches click-through conversions', label: 'BS CTC', fmt: 'number' },
    { key: 'Branded Searches Rate', label: 'BS Rate', fmt: 'pct_raw' },
    { key: 'Effective cost per Branded Search', label: 'eCPBS', fmt: 'currency_raw' },
    { key: 'Long-Term Sales', label: 'Long-Term Sales', fmt: 'currency_raw' },
    { key: 'Long-Term ROAS', label: 'Long-Term ROAS', fmt: 'decimal_raw' },
  ],
  'Sponsored Display': [
    { key: 'campaign_name', label: 'Campaign Name', align: 'left' },
    { key: 'Portfolio name', label: 'Portfolio', align: 'left' },
    { key: 'Status', label: 'Status', align: 'left' },
    { key: 'Cost Type', label: 'Cost Type', align: 'left' },
    { key: 'Budget', label: 'Budget', fmt: 'currency_raw' },
    { key: 'Impressions', label: 'Impressions', fmt: 'number' },
    { key: 'Viewable Impressions', label: 'Viewable Impr.', fmt: 'number' },
    { key: 'Clicks', label: 'Clicks', fmt: 'number' },
    { key: 'Click-Through Rate (CTR)', label: 'CTR', fmt: 'pct_raw' },
    { key: 'Cost Per Click (CPC)', label: 'CPC', fmt: 'currency_raw' },
    { key: 'Cost per 1,000 viewable impressions (VCPM)', label: 'vCPM', fmt: 'currency_raw' },
    { key: 'ad_spend', label: 'Spend', fmt: 'currency' },
    { key: 'ad_spend_gst', label: 'Spend (Incl. GST)', fmt: 'currency', computed: true },
    { key: 'Total Advertising Cost of Sales (ACOS) ', label: 'ACOS', fmt: 'pct_raw' },
    { key: 'Total Return on Advertising Spend (ROAS)', label: 'ROAS', fmt: 'decimal_raw' },
    { key: '14 Day Detail Page Views (DPV)', label: 'DPV 14d', fmt: 'number' },
    { key: '14 Day Total Orders (#)', label: 'Orders 14d', fmt: 'number' },
    { key: '14 Day Total Units (#)', label: 'Units 14d', fmt: 'number' },
    { key: '14 Day Total Sales (₹)', label: 'Sales 14d', fmt: 'currency_raw' },
    { key: '14 Day New-to-brand Orders (#)', label: 'NTB Orders', fmt: 'number' },
    { key: '14 Day New-to-brand Sales (₹)', label: 'NTB Sales', fmt: 'currency_raw' },
    { key: '14 Day New-to-brand Units (#)', label: 'NTB Units', fmt: 'number' },
    { key: 'Total Advertising Cost of Sales (ACOS) – (Click)', label: 'ACOS (Click)', fmt: 'pct_raw' },
    { key: 'Total Return on Advertising Spend (ROAS) – (Click)', label: 'ROAS (Click)', fmt: 'decimal_raw' },
    { key: '14 Day Total Orders (#) – (Click)', label: 'Orders 14d (Click)', fmt: 'number' },
    { key: '14 Day Total Units (#) – (Click)', label: 'Units 14d (Click)', fmt: 'number' },
    { key: '14 Day Total Sales – (Click)', label: 'Sales 14d (Click)', fmt: 'currency_raw' },
    { key: '14 Day New-to-brand Orders (#) – (Click)', label: 'NTB Orders (Click)', fmt: 'number' },
    { key: '14 Day New-to-brand Sales - (Click)', label: 'NTB Sales (Click)', fmt: 'currency_raw' },
    { key: '14 Day New-to-brand Units (#) – (Click)', label: 'NTB Units (Click)', fmt: 'number' },
    { key: 'New-to-brand detail page views', label: 'NTB DPV', fmt: 'number' },
    { key: 'New-to-brand detail page view view-through conversions', label: 'NTB DPV VTC', fmt: 'number' },
    { key: 'New-to-brand detail page view click-through conversions', label: 'NTB DPV CTC', fmt: 'number' },
    { key: 'New-to-brand detail page view rate', label: 'NTB DPV Rate', fmt: 'pct_raw' },
    { key: 'Effective cost per new-to-brand detail page view', label: 'eCPNTB-DPV', fmt: 'currency_raw' },
    { key: '14 Day ATC', label: 'ATC 14d', fmt: 'number' },
    { key: '14 Day ATC Views', label: 'ATC Views', fmt: 'number' },
    { key: '14 Day ATC Clicks', label: 'ATC Clicks', fmt: 'number' },
    { key: '14 Day ATCR', label: 'ATCR 14d', fmt: 'pct_raw' },
    { key: 'Effective cost per Add to Cart (eCPATC)', label: 'eCPATC', fmt: 'currency_raw' },
    { key: '14 Day Branded Searches', label: 'Branded Searches', fmt: 'number' },
    { key: 'Branded Searches view-through conversions', label: 'BS VTC', fmt: 'number' },
    { key: 'Branded Searches click-through conversions', label: 'BS CTC', fmt: 'number' },
    { key: 'Branded Searches Rate', label: 'BS Rate', fmt: 'pct_raw' },
    { key: 'Effective cost per Branded Search', label: 'eCPBS', fmt: 'currency_raw' },
    { key: 'Long-Term Sales', label: 'Long-Term Sales', fmt: 'currency_raw' },
    { key: 'Long-Term ROAS', label: 'Long-Term ROAS', fmt: 'decimal_raw' },
  ],
  '3P DSP': [
    { key: 'campaign_name', label: 'Campaign name', align: 'left' },
    { key: 'ad_spend', label: 'Ad spend', fmt: 'currency' },
    { key: 'views', label: 'Impressions', fmt: 'number' },
    { key: 'clicks', label: 'Clicks', fmt: 'number' },
    { key: 'ctr', label: 'CTR', fmt: 'pct' },
    { key: 'total_revenue', label: 'Sales', fmt: 'currency' },
    { key: 'roi', label: 'ROAS', fmt: 'decimal' },
    { key: 'acos', label: 'ACOS', fmt: 'pct' },
  ],
  // Telegram Ads — drill view (post-aggregation rows: Telegram Group + SKU)
  'Telegram Ads': [
    { key: 'campaign_name', label: 'Campaign', align: 'left' },
    { key: 'telegram_group', label: 'Telegram Group', align: 'left' },
    { key: 'brand', label: 'Brand', align: 'left' },
    { key: 'sku_id', label: 'SKU ID', align: 'left' },
    { key: 'post_type', label: 'Post Type', align: 'left' },
    { key: 'nop', label: 'No. of Posts', fmt: 'number' },
    { key: 'ad_spend', label: 'Spends', fmt: 'currency' },
    { key: 'ad_spend_gst', label: 'Spends (GST)', fmt: 'currency' },
  ],
  // Google Ads — Product Report drill view
  'Google Ads': [
    { key: 'product_title', label: 'Product Title', align: 'left' },
    { key: 'brand', label: 'Brand', align: 'left' },
    { key: 'product_item_id', label: 'Item ID', align: 'left' },
    { key: 'ad_spend', label: 'Cost', fmt: 'currency' },
    { key: 'views', label: 'Views', fmt: 'number' },
    { key: 'clicks', label: 'Clicks', fmt: 'number' },
    { key: 'ctr', label: 'CTR', fmt: 'pct' },
    { key: 'units_sold', label: 'Units Sold', fmt: 'number' },
    { key: 'cvr', label: 'CVR', fmt: 'pct' },
    { key: 'total_revenue', label: 'Sales', fmt: 'currency' },
    { key: 'cpc', label: 'CPC', fmt: 'currency' },
    { key: 'roi', label: 'ROAS', fmt: 'decimal' },
  ],
};

// Channel-scoped column overrides. When the same `ad_product` label (e.g.
// "Sponsored Products") exists on multiple channels with different file
// schemas, CAMPAIGN_COLS_BY_CHANNEL takes precedence over the global
// CAMPAIGN_COLS. Today: Amazon vs Zepto. Keys are looked up by lowercased
// channel name.
//
// Zepto note: campaign-level metadata (campaign_type / status / daily_budget /
// cpm / robas / roas_native) is parsed into `row.extra` server-side and then
// FLATTENED onto the row root by /ad-properties/data — so columns here read
// these as plain flat keys.
const ZEPTO_SP_COLS = [
  { key: 'campaign_name',    label: 'Campaign Name', align: 'left' },
  { key: 'brand',            label: 'Brand', align: 'left' },
  { key: 'campaign_type',    label: 'Campaign Type', align: 'left' },
  { key: 'status',           label: 'Status', align: 'left' },
  { key: 'daily_budget',     label: 'Daily Budget', fmt: 'currency' },
  { key: 'views',            label: 'Impressions', fmt: 'number' },
  { key: 'clicks',           label: 'Clicks', fmt: 'number' },
  { key: 'ctr',              label: 'CTR', fmt: 'pct' },
  { key: 'cpc',              label: 'CPC', fmt: 'currency' },
  { key: 'cpm',              label: 'CPM', fmt: 'currency' },
  { key: 'ad_spend',         label: 'Spend', fmt: 'currency' },
  { key: 'ad_spend_gst',     label: 'Spend (Incl. GST)', fmt: 'currency' },
  { key: 'total_revenue',    label: 'Revenue', fmt: 'currency' },
  { key: 'roas_native',      label: 'ROAS', fmt: 'decimal' },
  { key: 'robas',            label: 'ROBAS', fmt: 'decimal' },
  { key: 'acos',             label: 'ACOS', fmt: 'pct' },
  { key: 'total_converted_units', label: 'Orders', fmt: 'number' },
  { key: 'add_to_carts',     label: 'ATC', fmt: 'number' },
  { key: 'cvr',              label: 'CVR', fmt: 'pct' },
  { key: 'same_skus',        label: 'Same SKUs', fmt: 'number' },
  { key: 'other_skus',       label: 'Other SKUs', fmt: 'number' },
];

const ZEPTO_SB_COLS = ZEPTO_SP_COLS.filter(c => c.key !== 'cpc');

// Blinkit Performance products (Product Booster / Recommendation Ads /
// Product Shelf) — file carries conversion metrics. RoAS is preserved
// pre-GST from the source's `Total RoAS` column via the parser's
// `roas_native` mirror (here read via the flattened `total_roas` key).
const BLINKIT_PERFORMANCE_COLS = [
  { key: 'campaign_name',    label: 'Campaign Name', align: 'left' },
  { key: 'brand',            label: 'Brand', align: 'left' },
  { key: 'targeting_type',   label: 'Targeting', align: 'left' },
  { key: 'pacing_type',      label: 'Pacing', align: 'left' },
  { key: 'views',            label: 'Impressions', fmt: 'number' },
  { key: 'add_to_carts',     label: 'ATC', fmt: 'number' },
  { key: 'total_converted_units', label: 'Qty Sold', fmt: 'number' },
  { key: 'total_revenue',    label: 'Direct Sales', fmt: 'currency' },
  { key: 'direct_roas',      label: 'Direct RoAS', fmt: 'decimal' },
  { key: 'total_roas',       label: 'Total RoAS', fmt: 'decimal' },
  { key: 'new_users_acquired', label: 'New Users', fmt: 'number' },
  { key: 'ad_spend',         label: 'Spend', fmt: 'currency' },
  { key: 'ad_spend_gst',     label: 'Spend (Incl. GST)', fmt: 'currency' },
  { key: 'acos',             label: 'ACOS', fmt: 'pct' },
];

// Blinkit Reach products (Stories / Brand Booster / Listing Spotlight) —
// reach-style placements; no conversion fields in source.
const BLINKIT_REACH_COLS = [
  { key: 'campaign_name',    label: 'Campaign Name', align: 'left' },
  { key: 'brand',            label: 'Brand', align: 'left' },
  { key: 'targeting_type',   label: 'Targeting', align: 'left' },
  { key: 'match_type',       label: 'Match Type', align: 'left' },
  { key: 'views',            label: 'Impressions', fmt: 'number' },
  { key: 'reach',            label: 'Reach', fmt: 'number' },
  { key: 'ctr',              label: 'CTR', fmt: 'pct' },
  { key: 'clicks',           label: 'Unique Clicks', fmt: 'number' },
  { key: 'cpm',              label: 'CPM', fmt: 'currency' },
  { key: 'ad_spend',         label: 'Spend', fmt: 'currency' },
  { key: 'ad_spend_gst',     label: 'Spend (Incl. GST)', fmt: 'currency' },
];

// Blinkit Hybrid products (Prime Banner / Brand Spotlight) — file carries
// both reach-only AND conversion fields, so the column set is the union.
const BLINKIT_HYBRID_COLS = [
  { key: 'campaign_name',    label: 'Campaign Name', align: 'left' },
  { key: 'brand',            label: 'Brand', align: 'left' },
  { key: 'targeting_type',   label: 'Targeting', align: 'left' },
  { key: 'pacing_type',      label: 'Pacing', align: 'left' },
  { key: 'views',            label: 'Impressions', fmt: 'number' },
  { key: 'reach',            label: 'Reach', fmt: 'number' },
  { key: 'ctr',              label: 'CTR', fmt: 'pct' },
  { key: 'clicks',           label: 'Unique Clicks', fmt: 'number' },
  { key: 'add_to_carts',     label: 'ATC', fmt: 'number' },
  { key: 'total_converted_units', label: 'Qty Sold', fmt: 'number' },
  { key: 'total_revenue',    label: 'Direct Sales', fmt: 'currency' },
  { key: 'total_roas',       label: 'Total RoAS', fmt: 'decimal' },
  { key: 'new_users_acquired', label: 'New Users', fmt: 'number' },
  { key: 'cpm',              label: 'CPM', fmt: 'currency' },
  { key: 'ad_spend',         label: 'Spend', fmt: 'currency' },
  { key: 'ad_spend_gst',     label: 'Spend (Incl. GST)', fmt: 'currency' },
];

const CAMPAIGN_COLS_BY_CHANNEL = {
  zepto: {
    'Sponsored Products': ZEPTO_SP_COLS,
    'Sponsored Brand':    ZEPTO_SB_COLS,
    'Sponsored Display':  ZEPTO_SP_COLS,
  },
  blinkit: {
    'Product Booster':    BLINKIT_PERFORMANCE_COLS,
    'Recommendation Ads': BLINKIT_PERFORMANCE_COLS,
    'Product Shelf':      BLINKIT_PERFORMANCE_COLS,
    'Stories':            BLINKIT_REACH_COLS,
    'Listing Spotlight':  BLINKIT_REACH_COLS,
    'Brand Booster':      BLINKIT_REACH_COLS,
    'Prime Banner':       BLINKIT_HYBRID_COLS,
    'Brand Spotlight':    BLINKIT_HYBRID_COLS,
  },
};

// Helper — reads a possibly-dotted key path off a row (e.g. "extra.status").
// Kept for forward-compat with any column that wants nested access; current
// Zepto columns read flat keys because /ad-properties/data flattens `extra`.
const _readKeyPath = (row, key) => {
  if (!row || !key) return undefined;
  if (!key.includes('.')) return row[key];
  return key.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), row);
};


export default function AdProperties({ user }) {
  const [headerFilters, setHeaderFilters] = useState({});
  const { brandIcon, platformIcon } = useMetadataIcons();
  const [loading, setLoading] = useState(false);
  const [adData, setAdData] = useState({});
  const [prevAdData, setPrevAdData] = useState({}); // Previous period comparison data
  const [prevPeriodLabel, setPrevPeriodLabel] = useState('');
  const [summaryData, setSummaryData] = useState([]);
  const [drillProduct, setDrillProduct] = useState(null); // null = summary view, string = campaign drill
  // Telegram Ads cascading drill state (only active when drillProduct === 'Telegram Ads')
  const [drillTgGroup, setDrillTgGroup] = useState(null);
  const [drillTgBrand, setDrillTgBrand] = useState(null);
  // Google Ads cascading drill state (Campaign → Brand → Product)
  const [drillGoogleCampaign, setDrillGoogleCampaign] = useState(null);
  const [drillGoogleBrand, setDrillGoogleBrand] = useState(null);
  // 3P DSP cascading drill state (Brand → Campaign)
  const [drill3PDspBrand, setDrill3PDspBrand] = useState(null);
  const [drillOnoDspBrand, setDrillOnoDspBrand] = useState(null);  // Amazon ONO DSP: Brand-then-Campaign drill
  // D2C extra drill layer — when the user clicks a D2C ad-channel row (Meta Ads /
  // Google Ads / WhatsApp), this holds the picked ad-channel name. Rendering then
  // restricts to that ad-channel's section. Reset when channel changes.
  const [drillD2CAdChannel, setDrillD2CAdChannel] = useState(null);
  // Blinkit cascading drill state: top-level "On-Platform Ads" summary →
  // pick Performance or Reach → drill into that ad-type's products. Mirrors
  // the D2C ad-channel drill (drillD2CAdChannel) but for Blinkit's nested
  // marketplace structure.
  const [drillBlinkitAdType, setDrillBlinkitAdType] = useState(null);
  // Visible error state for failed data loads — shown as a red banner with Retry.
  const [loadError, setLoadError] = useState(null);
  // Manually-entered Revenue overrides for Off-Platform Ads rows. Keyed by
  // `${ad_product}|${date_start}|${date_end}` so overrides are scoped to the visible date range.
  const [revenueOverrides, setRevenueOverrides] = useState({});
  const [drillDate, setDrillDate] = useState(null); // selected date filter from chart click
  const [previewProduct, setPreviewProduct] = useState(null); // for preview modal
  const [sortConfig, setSortConfig] = useState({ key: null, dir: null }); // sorting (3-state: asc/desc/null)
  const [campaignSort, setCampaignSort] = useState({ key: null, dir: null });
  const [visibleMetrics, setVisibleMetrics] = useState(['ad_spend_gst', 'roi', 'views', 'clicks', 'ctr', 'total_converted_units', 'cvr', 'acos', 'total_revenue']);
  // User-controlled card order within the metric cards row. Persists for this session.
  const [metricOrder, setMetricOrder] = useState([]);
  const [draggingKey, setDraggingKey] = useState(null);
  const [showMetricPicker, setShowMetricPicker] = useState(false);
  const [customMetrics, setCustomMetrics] = useState([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customFormula, setCustomFormula] = useState('');
  // Hide-graphs toggle — persisted so it survives reloads.
  const [hideGraphs, setHideGraphs] = useState(() => {
    try { return localStorage.getItem('trace_adprop_hide_graphs') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('trace_adprop_hide_graphs', hideGraphs ? '1' : '0'); } catch { /* ignore */ }
  }, [hideGraphs]);

  // Monthly comparison state
  const [comparedMonths, setComparedMonths] = useState([]); // ['2026-03','2026-02', ...]
  const [comparisonData, setComparisonData] = useState({}); // {monthKey: rows[]}
  const [availableMonths, setAvailableMonths] = useState([]); // ['2026-01','2026-02',...]

  const selectedChannel = headerFilters.channel || '';
  const selectedBrands = headerFilters.brands || [];
  const selectedExtras = headerFilters.extras || [];
  const startDate = headerFilters.start_date || '';
  const endDate = headerFilters.end_date || '';
  const dateMode = headerFilters.dateMode || 'daily';
  // TACOS option was removed — coerce any saved 'tacos' selection back to 'acos'.
  const rawThirdGraph = headerFilters.thirdGraph || 'acos';
  const thirdGraph = rawThirdGraph === 'tacos' ? 'acos' : rawThirdGraph;
  const isUnlocked = headerFilters.isUnlocked || false;

  const channelData = selectedChannel ? AD_CHANNEL_MAP[selectedChannel] || {} : {};
  // For D2C, after the user drills into a specific ad-channel (e.g. Meta Ads),
  // restrict the rendered sections to just that one. Default summary view shows
  // the new D2C ad-channel summary table (rendered separately below).
  const isD2C = selectedChannel === 'D2C';
  const isBlinkit = selectedChannel === 'Blinkit';
  // Filter ad_types based on per-channel intermediate drill:
  //   D2C     → restrict to the picked ad-channel (Meta Ads / Google Ads / WhatsApp)
  //   Blinkit → restrict to the picked ad-type (Performance / Reach); default
  //             view shows the wrapper table only (no inner product tables yet)
  const adTypes = (isD2C && drillD2CAdChannel)
    ? [drillD2CAdChannel]
    : (isBlinkit && drillBlinkitAdType)
      ? [drillBlinkitAdType]
      : Object.keys(channelData);

  // Fetch data when channel or filters change
  useEffect(() => {
    if (isUnlocked && selectedChannel) {
      setDrillProduct(null);
      setDrillDate(null);
      setDrillTgGroup(null);
      setDrillTgBrand(null);
      setDrillGoogleCampaign(null);
      setDrillGoogleBrand(null);
      setDrill3PDspBrand(null);
      setDrillD2CAdChannel(null);
      setDrillBlinkitAdType(null);
      fetchAdData();
    }
  }, [selectedChannel, isUnlocked, selectedBrands.join(','), selectedExtras.join(','), startDate, endDate]); // eslint-disable-line

  // Reset Telegram cascade whenever the user steps in/out of any product drill
  useEffect(() => { setDrillTgGroup(null); setDrillTgBrand(null); setDrillGoogleCampaign(null); setDrillGoogleBrand(null); setDrill3PDspBrand(null); setDrillD2CAdChannel(null); setDrillBlinkitAdType(null); }, [drillProduct]);

  // On first mount only: default the date range to the PREVIOUS calendar
  // month (e.g. today 2026-02-13 → 2026-01-01 to 2026-01-31). This mirrors
  // the header default so both stay in lockstep.
  const didInitDateRef = useRef(false);
  useEffect(() => {
    if (didInitDateRef.current) return;
    if (!isUnlocked || !selectedChannel) return;
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    const toISO = (d) => d.toISOString().split('T')[0];
    didInitDateRef.current = true;
    setHeaderFilters(prev => ({ ...prev, start_date: toISO(start), end_date: toISO(end) }));
  }, [selectedChannel, isUnlocked]); // eslint-disable-line

  // Fetch revenue overrides for the active date range and merge into local state.
  const fetchRevenueOverrides = async () => {
    if (!startDate || !endDate) return;
    try {
      const r = await fetch(`${API}/ad-properties/revenue-overrides?date_start=${startDate}&date_end=${endDate}`, { credentials: 'include' });
      if (!r.ok) return;
      const data = await r.json();
      const map = {};
      for (const o of (data.overrides || [])) {
        map[`${o.ad_product}|${o.date_start}|${o.date_end}`] = Number(o.revenue || 0);
      }
      setRevenueOverrides(map);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchRevenueOverrides(); /* eslint-disable-next-line */ }, [startDate, endDate]);
  // Reset sort state when filters/date range change (per spec)
  useEffect(() => {
    setSortConfig({ key: null, dir: null });
    setCampaignSort({ key: null, dir: null });
  }, [startDate, endDate, selectedChannel, selectedBrands, selectedExtras]);

  const saveRevenueOverride = async (key, value) => {
    const [ad_product, ds, de] = key.split('|');
    try {
      const r = await fetch(`${API}/ad-properties/revenue-overrides`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ad_product, date_start: ds, date_end: de, revenue: value }),
      });
      if (!r.ok) throw new Error('save failed');
      setRevenueOverrides(prev => ({ ...prev, [key]: Number(value) }));
    } catch (e) { /* ignore */ }
  };

  const clearRevenueOverride = async (key) => {
    const [ad_product, ds, de] = key.split('|');
    try {
      await fetch(`${API}/ad-properties/revenue-overrides?ad_product=${encodeURIComponent(ad_product)}&date_start=${ds}&date_end=${de}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setRevenueOverrides(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (e) { /* ignore */ }
  };

  const fetchAdData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams({ channel: selectedChannel });
      if (selectedBrands.length > 0) params.set('brand', selectedBrands.join(','));
      if (selectedExtras.length > 0) params.set('ad_product', selectedExtras.join(','));
      if (startDate) params.set('date_start', startDate);
      if (endDate) params.set('date_end', endDate);

      // Calculate previous comparison period
      let prevParams = null;
      if (startDate && endDate) {
        const s = new Date(startDate);
        const e = new Date(endDate);
        const days = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
        const prevEnd = new Date(s);
        prevEnd.setDate(prevEnd.getDate() - 1);
        const prevStart = new Date(prevEnd);
        prevStart.setDate(prevStart.getDate() - days + 1);
        const fmt = (d) => d.toISOString().split('T')[0];
        prevParams = new URLSearchParams({ channel: selectedChannel });
        if (selectedBrands.length > 0) prevParams.set('brand', selectedBrands.join(','));
        if (selectedExtras.length > 0) prevParams.set('ad_product', selectedExtras.join(','));
        prevParams.set('date_start', fmt(prevStart));
        prevParams.set('date_end', fmt(prevEnd));
        setPrevPeriodLabel(`vs prev ${days} day${days > 1 ? 's' : ''}`);
      } else {
        setPrevPeriodLabel('');
      }

      // Fetch current + previous in parallel
      const [res, prevRes] = await Promise.all([
        fetch(`${API}/ad-properties/data?${params}`, { credentials: 'include' }),
        prevParams ? fetch(`${API}/ad-properties/data?${prevParams}`, { credentials: 'include' }) : Promise.resolve(null),
      ]);

      if (!res.ok) throw new Error('Failed');
      const result = await res.json();
      const grouped = {};
      for (const row of result.rows) {
        const prod = row.ad_product || 'Unknown';
        if (!grouped[prod]) grouped[prod] = [];
        grouped[prod].push(row);
      }
      setAdData(grouped);
      setSummaryData(result.summary);

      // Process previous period data
      if (prevRes && prevRes.ok) {
        const prevResult = await prevRes.json();
        const prevGrouped = {};
        for (const row of prevResult.rows) {
          const prod = row.ad_product || 'Unknown';
          if (!prevGrouped[prod]) prevGrouped[prod] = [];
          prevGrouped[prod].push(row);
        }
        setPrevAdData(prevGrouped);
      } else {
        setPrevAdData({});
      }
    } catch (e) {
      console.error('[AdProperties] failed to load data', e);
      setLoadError(e?.message || 'Failed to load ad properties data');
      setAdData({});
      setPrevAdData({});
    } finally { setLoading(false); }
  };

  // Current month derived from selected end date (fallback to today).
  // In Monthly mode this is the displayed "(current)" chip.
  const currentMonth = (endDate || new Date().toISOString().slice(0, 10)).substring(0, 7);

  // Helper — given YYYY-MM, return the date range that spans that month.
  const monthBounds = (ym) => {
    const [y, mm] = ym.split('-');
    const yNum = parseInt(y, 10);
    const mNum = parseInt(mm, 10);
    const last = new Date(yNum, mNum, 0).getDate();
    return { start: `${ym}-01`, end: `${ym}-${String(last).padStart(2, '0')}` };
  };

  // Set the page's base month: updates date range (drives data fetch) and resets compare chips.
  const setBaseMonth = (ym) => {
    if (!ym || ym.length < 7) return;
    const { start, end } = monthBounds(ym);
    setComparedMonths([ym]);
    setHeaderFilters(prev => ({ ...prev, start_date: start, end_date: end }));
  };

  // When user switches Daily → Monthly, snap the date range to today's calendar month.
  useEffect(() => {
    if (dateMode !== 'monthly') return;
    const today = new Date();
    const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    if (currentMonth !== thisMonth) {
      setBaseMonth(thisMonth);
    }
    // eslint-disable-next-line
  }, [dateMode]);

  // Fetch list of available months for the channel (for the picker)
  useEffect(() => {
    if (!isUnlocked || !selectedChannel) return;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API}/ad-properties/available-months?channel=${encodeURIComponent(selectedChannel)}`, { credentials: 'include' });
        if (!res.ok) return;
        const { months } = await res.json();
        if (!cancel) setAvailableMonths(months || []);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [selectedChannel, isUnlocked]);

  // Reset comparison whenever channel or current month changes
  useEffect(() => {
    setComparedMonths(currentMonth ? [currentMonth] : []);
    setComparisonData({});
  }, [selectedChannel, currentMonth]);

  // Fetch any compared month's data that we don't yet have cached
  useEffect(() => {
    if (dateMode !== 'monthly') return;
    const missing = comparedMonths.filter(m => !comparisonData[m]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (const m of missing) {
        try {
          const params = new URLSearchParams();
          params.set('channel', selectedChannel);
          params.set('date_start', `${m}-01`);
          // Compute last day of month
          const [y, mm] = m.split('-');
          const last = new Date(parseInt(y, 10), parseInt(mm, 10), 0).getDate();
          params.set('date_end', `${m}-${String(last).padStart(2, '0')}`);
          if (selectedBrands.length > 0) selectedBrands.forEach(b => params.append('brand', b));
          if (selectedExtras.length > 0) selectedExtras.forEach(p => params.append('ad_product', p));
          const res = await fetch(`${API}/ad-properties/data?${params}`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            updates[m] = data.rows || [];
          } else {
            updates[m] = [];
          }
        } catch (e) { updates[m] = []; }
      }
      if (!cancelled) setComparisonData(prev => ({ ...prev, ...updates }));
    })();
    return () => { cancelled = true; };
  }, [comparedMonths, dateMode, selectedChannel, selectedBrands.join(','), selectedExtras.join(',')]); // eslint-disable-line

  const addCompareMonth = (m) => {
    if (comparedMonths.length >= 6) return;
    if (comparedMonths.includes(m)) return;
    setComparedMonths([...comparedMonths, m]);
  };
  const removeCompareMonth = (m) => {
    if (m === currentMonth) return;
    setComparedMonths(comparedMonths.filter(x => x !== m));
  };

  // ── Availability classification ─────────────────────────────────────────
  // At the property (ad_product) view we classify per-ad_product using the
  // dates present in each product's rows. At drill-down (drillProduct set) we
  // switch to per-campaign classification on the drilled product's rows.
  const availabilityByProduct = (() => {
    const out = {};
    for (const [prod, rows] of Object.entries(adData || {})) {
      out[prod] = classifyDates((rows || []).map(r => r.date));
    }
    return out;
  })();

  // For the drill-down view, per-campaign classification within the current product.
  const availabilityByCampaign = (() => {
    if (!drillProduct) return {};
    const rows = adData[drillProduct] || [];
    return classifyRowsByKey(rows, r => r.campaign_name || '');
  })();

  const hasDailyData = (rows) => {
    if (!rows || rows.length === 0) return false;
    const c = classifyDates(rows.map(r => r.date));
    return c.availability_type === 'date-wise' || c.availability_type === 'mixed';
  };

  // Helper: days in a calendar month (YYYY-MM string).
  const _daysInMonth = (yyyymm) => {
    const [y, m] = yyyymm.split('-').map(Number);
    return new Date(y, m, 0).getDate();
  };
  // Compute monthlyOnlyProps early so it's available to chart logic below.
  const monthlyOnlyProps = new Set(
    Object.entries(adData).filter(([, rows]) => !hasDailyData(rows)).map(([k]) => k)
  );

  // Chart data (DATE-WISE ONLY):
  //   • At summary view we sum ONLY rows whose parent product is date-wise or
  //     (for "mixed" products) whose date falls in a date-wise month.
  //   • At drill view we do the same on a per-campaign basis inside the
  //     currently drilled product.
  // Month-wise / week-wise rows are NEVER distributed or interpolated — they
  // simply do not contribute to the line chart. Table totals and metric cards
  // still include them.
  const getLineChartData = () => {
    let bucket = {};
    if (drillProduct) {
      const rows = adData[drillProduct] || [];
      const kept = keepDateWiseRows(rows, r => r.campaign_name || '', availabilityByCampaign);
      for (const r of kept) {
        if (!r.date) continue;
        if (!bucket[r.date]) bucket[r.date] = { spend: 0, revenue: 0 };
        bucket[r.date].spend   += r.ad_spend || 0;
        bucket[r.date].revenue += r.total_revenue || 0;
      }
    } else {
      for (const [prod, rows] of Object.entries(adData || {})) {
        const cls = availabilityByProduct[prod];
        if (!cls || (cls.availability_type !== 'date-wise' && cls.availability_type !== 'mixed')) continue;
        for (const r of rows || []) {
          if (!r.date) continue;
          if (cls.availability_type === 'mixed') {
            const m = String(r.date).substring(0, 7);
            if (!(cls.date_wise_ranges || []).some(x => x.month === m)) continue;
          }
          if (!bucket[r.date]) bucket[r.date] = { spend: 0, revenue: 0 };
          bucket[r.date].spend   += r.ad_spend || 0;
          bucket[r.date].revenue += r.total_revenue || 0;
        }
      }
    }

    const inWindow = (d) => !startDate || !endDate || (d >= startDate && d <= endDate);
    const sorted = Object.keys(bucket).filter(inWindow).sort();

    if (dateMode === 'monthly') {
      const byMonth = {};
      for (const d of sorted) {
        const m = d.substring(0, 7);
        if (!byMonth[m]) byMonth[m] = { spend: 0, revenue: 0 };
        byMonth[m].spend   += bucket[d].spend;
        byMonth[m].revenue += bucket[d].revenue;
      }
      return Object.entries(byMonth).map(([month, v]) => {
        const gs = v.spend * 1.18;
        return {
          date: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          rawDate: month,
          spends: Math.round(v.spend), sales: Math.round(v.revenue),
          acos: v.revenue > 0 ? +((gs / v.revenue) * 100).toFixed(1) : 0,
          tacos: v.revenue > 0 ? +((gs / (v.revenue * 1.15)) * 100).toFixed(1) : 0,
          roas: gs > 0 ? +(v.revenue / gs).toFixed(2) : 0,
          estimated: false,
        };
      });
    }

    return sorted.map((dateStr) => {
      const v = bucket[dateStr];
      const gs = v.spend * 1.18;
      const d = new Date(dateStr);
      return {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        rawDate: dateStr,
        spends: Math.round(v.spend), sales: Math.round(v.revenue),
        acos: v.revenue > 0 ? +((gs / v.revenue) * 100).toFixed(1) : 0,
        tacos: v.revenue > 0 ? +((gs / (v.revenue * 1.15)) * 100).toFixed(1) : 0,
        roas: gs > 0 ? +(v.revenue / gs).toFixed(2) : 0,
        estimated: false,
      };
    });
  };
  const lineData = getLineChartData();
  // Exclusion-based now; no whole-line estimation dashed pattern needed.
  const isWholeLineEstimated = false;

  // Dynamic caption + empty-state for the chart.
  const chartEntries = drillProduct
    ? Object.entries(availabilityByCampaign).map(([name, c]) => ({ name, type: c.availability_type }))
    : Object.entries(availabilityByProduct).map(([name, c]) => ({ name, type: c.availability_type }));
  const chartCaption = buildChartCaption(chartEntries);
  const hasAnyDateWise = chartEntries.some(e => e.type === 'date-wise' || e.type === 'mixed');

  // Custom dot for monthly comparison: colored by the month chip color
  const MonthCompareDot = (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    const color = colorForMonth(payload.monthKey, comparedMonths);
    return <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />;
  };

  // Monthly comparison: one data point per month. X-axis = months chronologically.
  // Each compared month chip adds an additional point to the line. Volume metrics summed,
  // rate metrics derived from sums (= weighted average).
  const buildMonthCompareData = () => {
    if (dateMode !== 'monthly') return [];
    const sortedMonths = [...comparedMonths].sort();
    return sortedMonths.map(m => {
      const rowsForMonth = comparisonData[m] || [];
      let scope = rowsForMonth;
      if (drillProduct) scope = rowsForMonth.filter(r => r.ad_product === drillProduct);
      let spend = 0, revenue = 0, views = 0, clicks = 0, units = 0;
      for (const r of scope) {
        spend += r.ad_spend || 0;
        revenue += r.total_revenue || 0;
        views += r.views || 0;
        clicks += r.clicks || 0;
        units += r.total_converted_units || 0;
      }
      const gst = spend * 1.18;
      return {
        monthKey: m,
        month: formatMonthChip(m),
        spends: Math.round(spend),
        sales: Math.round(revenue),
        ctr: views > 0 ? +(clicks / views * 100).toFixed(2) : 0,
        cvr: clicks > 0 ? +(units / clicks * 100).toFixed(2) : 0,
        acos: revenue > 0 ? +((gst / revenue) * 100).toFixed(2) : 0,
        tacos: revenue > 0 ? +((gst / (revenue * 1.15)) * 100).toFixed(2) : 0,
        roas: gst > 0 ? +(revenue / gst).toFixed(2) : 0,
      };
    });
  };
  const compareData = buildMonthCompareData();
  const isCompareMode = dateMode === 'monthly' && comparedMonths.length > 0 && compareData.length > 0;

  const fmtChart = v => v >= 1e7 ? `${(v/1e7).toFixed(2)}Cr` : v >= 1e5 ? `${(v/1e5).toFixed(1)}L` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : `${Math.round(v)}`;
  const fmtVal = (val, fmt) => {
    if (val === undefined || val === null || val === '') return '-';
    if (fmt === 'currency') return `Rs. ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    if (fmt === 'currency_raw') {
      const cleaned = String(val).replace(/[₹,\s]/g, '');
      const n = Number(cleaned); if (!isFinite(n)) return val;
      return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    }
    if (fmt === 'number') {
      const n = Number(val); if (!isFinite(n)) return val;
      return n.toLocaleString('en-IN');
    }
    if (fmt === 'pct') return `${Number(val).toFixed(2)}%`;
    if (fmt === 'pct_raw') {
      const cleaned = String(val).replace(/[%,\s]/g, '');
      let n = Number(cleaned); if (!isFinite(n)) return val;
      // If a fraction (<= 1.5), treat as decimal ratio and convert
      if (Math.abs(n) <= 1.5 && n !== 0) n = n * 100;
      return `${n.toFixed(2)}%`;
    }
    if (fmt === 'decimal') return Number(val).toFixed(2);
    if (fmt === 'decimal_raw') { const n = Number(val); return isFinite(n) ? n.toFixed(2) : val; }
    return val;
  };

  const graphs = [
    { key: 'spends', label: 'Spends', color: '#32B4AC', format: fmtChart },
    { key: 'sales', label: 'Revenue', color: '#6366f1', format: fmtChart },
  ];
  const thirdCfg = {
    acos: { key: 'acos', label: 'ACOS', color: '#f59e0b', format: v => `${v}%` },
    tacos: { key: 'tacos', label: 'TACOS', color: '#ef4444', format: v => `${v}%` },
    roas: { key: 'roas', label: 'ROI', color: '#10b981', format: v => `${v}x` }
  };
  const tg = thirdCfg[thirdGraph];

  const handleChartClick = (data) => {
    if (dateMode !== 'daily') return;
    const payload = data?.activePayload?.[0]?.payload;
    if (payload?.rawDate) {
      setDrillDate(payload.rawDate);
    }
  };

  // Custom active dot that handles click
  const ClickableDot = (props) => {
    const { cx, cy, payload, fill } = props;
    return (
      <circle cx={cx} cy={cy} r={6} fill={fill || '#32B4AC'} stroke="#fff" strokeWidth={2}
        style={{ cursor: dateMode === 'daily' ? 'pointer' : 'default' }}
        onClick={(e) => { e.stopPropagation(); if (dateMode === 'daily' && payload?.rawDate) setDrillDate(payload.rawDate); }} />
    );
  };

  // 3-state sort cycle: asc → desc → unsorted (default order)
  const toggleSort = (key, setter, current) => {
    if (current.key !== key) {
      setter({ key, dir: 'asc' });
      return;
    }
    if (current.dir === 'asc') setter({ key, dir: 'desc' });
    else setter({ key: null, dir: null });
  };
  const SortIcon = ({ colKey, config }) => {
    if (config.key !== colKey) return <ArrowUpDown className="w-3 h-3 text-gray-300 ml-0.5 inline opacity-60 group-hover:opacity-100" />;
    return config.dir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-[#32B4AC] ml-0.5 inline" />
      : <ArrowDown className="w-3 h-3 text-[#32B4AC] ml-0.5 inline" />;
  };
  // Numeric-aware compare: parse numbers when possible, else case-insensitive string.
  const _sortCmp = (av, bv) => {
    const an = typeof av === 'number' ? av : parseFloat(av);
    const bn = typeof bv === 'number' ? bv : parseFloat(bv);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    return String(av ?? '').toLowerCase().localeCompare(String(bv ?? '').toLowerCase());
  };
  const applySummarySort = (rows) => {
    if (!sortConfig.key) return rows;
    const sorted = [...rows].sort((a, b) => _sortCmp(a[sortConfig.key], b[sortConfig.key]));
    return sortConfig.dir === 'desc' ? sorted.reverse() : sorted;
  };

  const getExtraOptions = (channel) => {
    if (!channel) return [];
    const data = AD_CHANNEL_MAP[channel] || {};
    return Object.values(data).flat();
  };
  const pageFilters = { channels: CHANNELS, channelGated: true, extraLabel: 'Ad Product', getExtraOptions };

  const hasData = Object.keys(adData).length > 0;

  // Apply date filter from chart click + exclude monthly-only properties when in Daily mode
  const filteredAdData = (() => {
    let entries = Object.entries(adData);
    if (drillDate) entries = entries.map(([k, rows]) => [k, rows.filter(r => r.date === drillDate)]);
    // CHANGE 2: never hide monthly-only properties — show row with "—" instead.
    return Object.fromEntries(entries);
  })();

  // CHANGE 3 helper — single-day mode = startDate === endDate.
  const isSingleDay = !!(startDate && endDate && startDate === endDate);

  // Build summary rows for the main table
  const buildSummaryRows = () => {
    const allProps = adTypes.flatMap(t => channelData[t] || []);
    return allProps.map(prop => {
      const rows = filteredAdData[prop] || [];
      const spend = rows.reduce((s, r) => s + (r.ad_spend || 0), 0);
      const sales = rows.reduce((s, r) => s + (r.total_revenue || 0), 0);
      const gstSpend = spend * 1.18;
      const roi = gstSpend > 0 ? sales / gstSpend : 0;
      const acos = sales > 0 ? (gstSpend / sales) * 100 : 0;
      return { name: prop, spends: spend, sales, roi, acos, count: rows.length };
    });
  };
  const summaryRows = isUnlocked ? buildSummaryRows() : [];
  const totalSpends = summaryRows.reduce((s, r) => s + r.spends, 0);
  const totalSales = summaryRows.reduce((s, r) => s + r.sales, 0);

  // Aggregate campaign rows (sum across dates)
  // Sums standard metric fields + ALL native numeric columns (Amazon's many sub-metrics).
  // Rate / ratio columns are recomputed in the totals row, not summed here.
  const STANDARD_SUM_FIELDS = new Set(['ad_spend','views','clicks','total_converted_units','total_revenue','converted_units_view','converted_units_clicks','revenue_units_view','revenue_units_clicks','add_to_carts','same_skus','other_skus','reach','direct_atc','indirect_atc','direct_quantities_sold','indirect_quantities_sold','direct_sales','indirect_sales','new_users_acquired']);
  const RATE_KEYWORDS = ['rate','ctr','cvr','acos','roas','robas','vcpm','cpc','cpm','cpi','%','ratio','budget','daily_'];
  const isNumericField = (val) => {
    if (val === undefined || val === null || val === '') return false;
    if (typeof val === 'number') return true;
    const s = String(val).replace(/[₹,%\s]/g, '');
    return s !== '' && !isNaN(Number(s));
  };
  const isRateLikeKey = (k) => {
    const lk = k.toLowerCase();
    return RATE_KEYWORDS.some(w => lk.includes(w));
  };
  const cleanNum = (v) => {
    if (v === undefined || v === null || v === '') return 0;
    if (typeof v === 'number') return v;
    const n = Number(String(v).replace(/[₹,%\s]/g, ''));
    return isFinite(n) ? n : 0;
  };
  const aggregateRows = (rows) => {
    const map = {};
    for (const r of rows) {
      const key = r.campaign_name + '||' + (r.ad_group || '');
      if (!map[key]) {
        // Seed with first row, but normalize summable numeric fields to numbers
        const seed = { ...r, _count: 1 };
        for (const k of Object.keys(seed)) {
          if (STANDARD_SUM_FIELDS.has(k)) seed[k] = cleanNum(seed[k]);
          else if (typeof seed[k] === 'string' && !isRateLikeKey(k) && isNumericField(seed[k])) seed[k] = cleanNum(seed[k]);
        }
        map[key] = seed;
      } else {
        const target = map[key];
        for (const k of Object.keys(r)) {
          if (STANDARD_SUM_FIELDS.has(k)) {
            target[k] = (target[k] || 0) + cleanNum(r[k]);
          } else if (!isRateLikeKey(k) && isNumericField(r[k])) {
            target[k] = cleanNum(target[k]) + cleanNum(r[k]);
          }
        }
        target._count += 1;
      }
    }
    return Object.values(map).map(r => ({
      ...r,
      ctr: r.views > 0 ? (r.clicks / r.views) * 100 : 0,
      cvr: r.clicks > 0 ? (r.total_converted_units / r.clicks) * 100 : 0,
      roi: r.ad_spend > 0 ? r.total_revenue / (r.ad_spend * 1.18) : 0,
      acos: r.total_revenue > 0 ? ((r.ad_spend * 1.18) / r.total_revenue) * 100 : 0,
      ad_spend_gst: (r.ad_spend || 0) * 1.18,
    }));
  };

  // ==================== METRIC CARDS ====================
  // 'ad_spend' (pre-GST) card removed from defaults — a single GST-inclusive
  // "Spends (Incl. GST)" card is the canonical spend surface. ACOS is
  // positioned right after CVR so the funnel order (Spend → Rev → ratios →
  // efficiency) reads naturally. Pre-GST key kept in ALL_METRICS so users
  // who need it can still add it back from the metric picker.
  const ALL_METRICS = [
    { key: 'ad_spend_gst', label: 'Spends (Incl. GST)', fmt: 'currency', field: 'ad_spend_gst' },
    { key: 'roi', label: 'ROI', fmt: 'decimal', computed: true },
    { key: 'views', label: 'Views', fmt: 'number', field: 'views' },
    { key: 'clicks', label: 'Clicks', fmt: 'number', field: 'clicks' },
    { key: 'ctr', label: 'CTR', fmt: 'pct', computed: true },
    { key: 'total_converted_units', label: 'Units Sold', fmt: 'number', field: 'total_converted_units' },
    { key: 'cvr', label: 'CVR', fmt: 'pct', computed: true },
    { key: 'acos', label: 'ACOS', fmt: 'pct', computed: true },
    { key: 'total_revenue', label: 'Revenue', fmt: 'currency', field: 'total_revenue' },
    { key: 'ad_spend', label: 'Spends (Pre-GST)', fmt: 'currency', field: 'ad_spend' },
  ];

  const computeMetrics = (data) => {
    const rows = Object.values(data).flat();
    const spend = rows.reduce((s, r) => s + (r.ad_spend || 0), 0);
    const views = rows.reduce((s, r) => s + (r.views || 0), 0);
    const clicks = rows.reduce((s, r) => s + (r.clicks || 0), 0);
    const units = rows.reduce((s, r) => s + (r.total_converted_units || 0), 0);
    const revenue = rows.reduce((s, r) => s + (r.total_revenue || 0), 0);
    const gstSpend = spend * 1.18;
    return {
      ad_spend: spend, ad_spend_gst: gstSpend, views, clicks, total_converted_units: units, total_revenue: revenue,
      roi: gstSpend > 0 ? revenue / gstSpend : 0,
      ctr: views > 0 ? (clicks / views) * 100 : 0,
      cvr: clicks > 0 ? (units / clicks) * 100 : 0,
      acos: revenue > 0 ? (gstSpend / revenue) * 100 : 0,
    };
  };

  const currentMetrics = computeMetrics(filteredAdData);
  // Compare against previous equivalent period
  const prevFilteredAdData = drillDate
    ? Object.fromEntries(Object.entries(prevAdData).map(([k, rows]) => [k, rows.filter(r => r.date === drillDate)]))
    : prevAdData;
  const prevMetrics = Object.keys(prevAdData).length > 0 ? computeMetrics(drillProduct ? { [drillProduct]: prevFilteredAdData[drillProduct] || [] } : prevFilteredAdData) : null;

  const fmtMetric = (val, fmt) => {
    if (val === undefined || val === null) return '-';
    if (fmt === 'currency') {
      if (val >= 1e7) return `Rs. ${(val / 1e7).toFixed(2)}Cr`;
      if (val >= 1e5) return `Rs. ${(val / 1e5).toFixed(2)}L`;
      if (val >= 1e3) return `Rs. ${(val / 1e3).toFixed(1)}K`;
      return `Rs. ${Math.round(val).toLocaleString('en-IN')}`;
    }
    if (fmt === 'number') {
      if (val >= 1e7) return `${(val / 1e7).toFixed(2)}Cr`;
      if (val >= 1e5) return `${(val / 1e5).toFixed(2)}L`;
      if (val >= 1e3) return `${(val / 1e3).toFixed(2)}K`;
      return Math.round(val).toLocaleString('en-IN');
    }
    if (fmt === 'pct') return `${val.toFixed(2)}%`;
    if (fmt === 'decimal') return val.toFixed(2);
    return val;
  };

  // Evaluate custom metric formula safely
  const evalCustom = (formula, data) => {
    try {
      const vars = { spend: (data.ad_spend || 0) * 1.18, revenue: data.total_revenue || 0, views: data.views || 0, clicks: data.clicks || 0, units: data.total_converted_units || 0, ctr: data.ctr || 0, cvr: data.cvr || 0, roi: data.roi || 0, acos: data.acos || 0 };
      const fn = new Function(...Object.keys(vars), `return ${formula}`);
      const result = fn(...Object.values(vars));
      return isFinite(result) ? result : 0;
    } catch { return 0; }
  };

  const MetricCards = ({ data, prev }) => {
    const allMetrics = [...ALL_METRICS, ...customMetrics];
    // Apply user-controlled order: keys in metricOrder come first (in that order),
    // then any remaining visible metrics in their original order. Filter to visible.
    const visibleSet = new Set(visibleMetrics);
    const orderedKeys = [
      ...metricOrder.filter(k => visibleSet.has(k)),
      ...visibleMetrics.filter(k => !metricOrder.includes(k)),
    ];
    const metrics = orderedKeys
      .map(k => allMetrics.find(m => m.key === k))
      .filter(Boolean);

    const onDragStart = (e, key) => {
      setDraggingKey(key);
      e.dataTransfer.effectAllowed = 'move';
      // Some browsers need data set for drag to fire; opaque value is fine.
      try { e.dataTransfer.setData('text/plain', key); } catch {}
    };
    const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
    const onDrop = (e, targetKey) => {
      e.preventDefault();
      const src = draggingKey;
      setDraggingKey(null);
      if (!src || src === targetKey) return;
      setMetricOrder(prev => {
        const base = prev.length ? prev : visibleMetrics;
        // Drop unknown keys (e.g. metrics removed via picker) before reordering.
        const cleaned = base.filter(k => visibleSet.has(k));
        // Ensure both src and targetKey are in the list (newly-visible metrics may not be).
        const ensured = [...cleaned];
        if (!ensured.includes(src)) ensured.push(src);
        if (!ensured.includes(targetKey)) ensured.push(targetKey);
        const without = ensured.filter(k => k !== src);
        const idx = without.indexOf(targetKey);
        without.splice(idx, 0, src);
        return without;
      });
    };

    return (
      <div className="flex gap-3 overflow-x-auto pb-1" data-testid="metric-cards">
        {metrics.map(m => {
          const val = m.formula ? evalCustom(m.formula, data) : (data[m.key] ?? 0);
          const prevVal = prev ? (m.formula ? evalCustom(m.formula, prev) : prev[m.key]) : null;
          const pctChange = prevVal && prevVal !== 0 ? ((val - prevVal) / Math.abs(prevVal)) * 100 : null;
          const isDragging = draggingKey === m.key;
          return (
            <div
              key={m.key}
              draggable
              onDragStart={(e) => onDragStart(e, m.key)}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, m.key)}
              onDragEnd={() => setDraggingKey(null)}
              className={`bg-white border rounded-xl px-4 py-3 shadow-sm min-w-[160px] flex-shrink-0 transition-all duration-150 cursor-grab active:cursor-grabbing select-none
                ${isDragging ? 'opacity-50 border-[#32B4AC] shadow-lg scale-[1.02]' : 'border-gray-200/80 hover:shadow-md hover:-translate-y-0.5'}`}
              data-testid={`metric-${m.key}`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <MetricTooltip label={m.key} />
                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{m.label}</span>
              </div>
              <div className="text-xl font-bold text-[#0F0F0F] leading-tight tabular-nums tracking-tight">{fmtMetric(val, m.fmt || 'number')}</div>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {pctChange !== null && (
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-semibold ${pctChange >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                    <span className="text-[10px] leading-none">{pctChange >= 0 ? '↑' : '↓'}</span>{Math.abs(pctChange).toFixed(2)}%
                  </span>
                )}
                {prevVal !== null ? (
                  <span className="text-[10.5px] text-gray-400 font-medium tabular-nums">Prev: {fmtMetric(prevVal, m.fmt || 'number')}</span>
                ) : prevPeriodLabel ? (
                  <span className="text-[10.5px] text-gray-400 font-medium italic">No prev data</span>
                ) : null}
              </div>
              {prevPeriodLabel && (
                <div className="text-[10px] text-gray-400 mt-0.5">{prevPeriodLabel}</div>
              )}
            </div>
          );
        })}
        <button onClick={() => setShowMetricPicker(true)}
          className="bg-white border-2 border-dashed border-gray-200 rounded-xl px-4 py-3 min-w-[120px] flex-shrink-0 flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-colors"
          data-testid="add-remove-metrics-btn">
          <Plus className="w-5 h-5" />
          <span className="text-[10px] font-medium">Add/Remove<br/>Metrics</span>
        </button>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar user={user} currentPage="ad-properties" />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          user={user}
          filters={headerFilters}
          onFiltersChange={setHeaderFilters}
          pageFilters={pageFilters}
          hideComparison
          monthControl={isUnlocked && dateMode === 'monthly' ? (
            <MonthCompare
              availableMonths={availableMonths}
              comparedMonths={comparedMonths}
              currentMonth={currentMonth}
              onAdd={addCompareMonth}
              onRemove={removeCompareMonth}
              onChangeCurrent={setBaseMonth}
            />
          ) : null}
        />
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-[1400px] mx-auto space-y-5">
            {/* Title & Breadcrumbs */}
            <div>
              <h1 className="text-2xl font-bold text-[#0F0F0F] tracking-tight mb-1">Ad Format Breakdown</h1>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <button onClick={() => { setDrillProduct(null); setDrillDate(null); }} className="hover:text-[#32B4AC]">Ad Format Breakdown</button>
                {drillProduct && (
                  <><ChevronRight className="w-3 h-3" /><span className="text-[#0F0F0F] font-medium">{drillProduct}</span></>
                )}
              </div>
            </div>

            {/* Visible load-error banner — replaces the previously silent catch. */}
            {loadError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3" data-testid="ad-properties-error">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">!</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-red-800">Failed to load Ad Format Breakdown data</div>
                  <div className="text-xs text-red-700 mt-0.5 truncate" title={loadError}>{loadError}</div>
                </div>
                <button
                  onClick={() => fetchAdData()}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                  data-testid="ad-properties-error-retry"
                >
                  Retry
                </button>
              </div>
            )}

            {!isUnlocked ? (
              <div className="bg-white border border-gray-200 border-dashed rounded-xl p-14 flex flex-col items-center justify-center text-center">
                <Lock className="w-8 h-8 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">Select a channel to view ad properties</p>
                <p className="text-gray-400 text-sm mt-1">Use the Channel filter above to get started</p>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-20"><div className="text-gray-500">Loading data...</div></div>
            ) : (
            <>
              {/* Month comparison chips now render inside the Header (above the
                  Daily/Monthly toggle); no in-page strip required. */}
              {/* Hide/Show graphs toggle */}
              <div className="flex justify-end -mb-2">
                <button
                  onClick={() => setHideGraphs(v => !v)}
                  data-testid="toggle-hide-graphs"
                  className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md px-2 py-1 transition-colors"
                  title={hideGraphs ? 'Show graphs' : 'Hide graphs'}
                >
                  {hideGraphs
                    ? <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg> Show graphs</>
                    : <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg> Hide graphs</>
                  }
                </button>
              </div>

              {/* Graphs — always show chart containers (unless user hid them) */}
              {!hideGraphs && !isCompareMode && chartCaption && (
                <p className="text-[11px] text-gray-500 -mt-1 mb-1" data-testid="adprop-chart-caption">{chartCaption}</p>
              )}
              {!hideGraphs && (!isCompareMode && !hasAnyDateWise && chartEntries.length > 0 ? (
                <div className="bg-white border border-amber-200 rounded-xl p-6 shadow-sm" data-testid="adprop-chart-empty-state">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                      <Info className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-[#0F0F0F]">No date-wise data available for this view</h3>
                      <p className="text-xs text-gray-500 mt-1">The {drillProduct ? 'campaigns' : 'properties'} at this level report month-wise or week-wise only. Aggregate totals still appear in the metric cards and the table below.</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {chartEntries.slice(0, 12).map((e) => (
                          <span key={e.name} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[11px] text-amber-800">
                            {e.name} · {e.type}
                          </span>
                        ))}
                        {chartEntries.length > 12 && <span className="text-[11px] text-gray-500">+ {chartEntries.length - 12} more</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {graphs.map(g => (
                  <div key={g.key} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">{g.label}</h3>
                    {isCompareMode ? (
                      <ResponsiveContainer width="100%" height={170}>
                        <LineChart data={compareData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                          <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} tickFormatter={g.format} />
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                            formatter={(v) => [g.format(v), g.label]} />
                          <Line type="monotone" dataKey={g.key} stroke="#37b6ad" strokeWidth={2.5}
                            dot={<MonthCompareDot />} activeDot={{ r: 6 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : lineData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={170}>
                        <LineChart data={lineData} onClick={handleChartClick} style={{ cursor: dateMode === 'daily' ? 'pointer' : 'default' }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                          <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} tickFormatter={g.format} />
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                            formatter={(v, _name, p) => [`${g.format(v)}${p?.payload?.estimated ? ' (estimated)' : ''}`, g.label]} />
                          <Line type="monotone" dataKey={g.key} stroke={g.color} strokeWidth={2.5}
                            strokeDasharray={isWholeLineEstimated ? '6 4' : undefined}
                            dot={{ r: 3, fill: g.color, stroke: g.color }} activeDot={<ClickableDot fill={g.color} />} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[170px] text-gray-300 text-sm">No data available</div>
                    )}
                    {dateMode === 'daily' && lineData.length > 0 && <p className="text-[10px] text-gray-400 mt-1 text-center">Click a data point to view day details</p>}
                    {isWholeLineEstimated && lineData.length > 0 && (
                      <p className="text-[10px] text-amber-700 mt-0.5 text-center flex items-center justify-center gap-1.5">
                        <span style={{ display:'inline-block', width:18, borderTop:'2px dashed #d97706' }}></span>
                        Estimated (monthly data distributed evenly)
                      </p>
                    )}
                  </div>
                ))}
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700">{tg.label}</h3>
                    <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                      {['acos', 'roas'].map(o => (
                        <button key={o} onClick={() => setHeaderFilters(p => ({ ...p, thirdGraph: o }))}
                          className={`px-2 py-1 text-[10px] font-semibold uppercase rounded transition-all ${thirdGraph === o ? 'bg-white text-[#0F0F0F] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>{o === 'roas' ? 'ROI' : o}</button>
                      ))}
                    </div>
                  </div>
                  {isCompareMode ? (
                    <ResponsiveContainer width="100%" height={170}>
                      <LineChart data={compareData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                        <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} tickFormatter={tg.format} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(v) => [tg.format(v), tg.label]} />
                        <Line type="monotone" dataKey={tg.key} stroke="#37b6ad" strokeWidth={2.5}
                          dot={<MonthCompareDot />} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : lineData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={170}>
                      <LineChart data={lineData} onClick={handleChartClick} style={{ cursor: dateMode === 'daily' ? 'pointer' : 'default' }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                        <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} tickFormatter={tg.format} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(v, _n, p) => [`${tg.format(v)}${p?.payload?.estimated ? ' (estimated)' : ''}`, tg.label]} />
                        <Line type="monotone" dataKey={tg.key} stroke={tg.color} strokeWidth={2.5}
                          strokeDasharray={isWholeLineEstimated ? '6 4' : undefined}
                          dot={{ r: 3, fill: tg.color, stroke: tg.color }} activeDot={<ClickableDot fill={tg.color} />} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[170px] text-gray-300 text-sm">No data available</div>
                  )}
                  {dateMode === 'daily' && lineData.length > 0 && <p className="text-[10px] text-gray-400 mt-1 text-center">Click a data point to view day details</p>}
                  {isWholeLineEstimated && lineData.length > 0 && (
                    <p className="text-[10px] text-amber-700 mt-0.5 text-center flex items-center justify-center gap-1.5">
                      <span style={{ display:'inline-block', width:18, borderTop:'2px dashed #d97706' }}></span>
                      Estimated (monthly data distributed evenly)
                    </p>
                  )}
                </div>
              </div>
              ))}

              {/* Active date filter badge */}
              {drillDate && (
                <div className="flex items-center gap-2" data-testid="date-filter-badge">
                  <div className="inline-flex items-center gap-2 bg-[#32B4AC]/10 border border-[#32B4AC]/30 rounded-lg px-3 py-1.5">
                    <Calendar className="w-3.5 h-3.5 text-[#32B4AC]" />
                    <span className="text-sm font-medium text-[#0F0F0F]">
                      {new Date(drillDate).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <button onClick={() => setDrillDate(null)} className="ml-1 p-0.5 rounded-full hover:bg-[#32B4AC]/20 transition-colors" data-testid="clear-date-filter">
                      <X className="w-3.5 h-3.5 text-[#32B4AC]" />
                    </button>
                  </div>
                  <span className="text-xs text-gray-400">Showing data for selected date only</span>
                </div>
              )}

              {/* ===== METRIC CARDS — above tables ===== */}
              {isUnlocked && !drillProduct && (
                <MetricCards data={currentMetrics} prev={prevMetrics} />
              )}

              {/* ===== D2C — extra "Ad Channel" summary drill layer =====
                  Shows Meta Ads / Google Ads / WhatsApp as clickable rows. Clicking
                  one sets `drillD2CAdChannel` and the standard per-ad-type summary
                  table below filters to just that ad-channel. */}
              {isUnlocked && isD2C && !drillProduct && !drillD2CAdChannel && (() => {
                const d2cAdChannels = Object.keys(channelData);
                // Per-ad-channel aggregation from already-filtered rows.
                const channelAgg = {};
                for (const adCh of d2cAdChannels) {
                  const productsInCh = channelData[adCh] || [];
                  let spend = 0, gst = 0, revenue = 0;
                  for (const p of productsInCh) {
                    const rows = filteredAdData[p] || [];
                    for (const r of rows) {
                      spend += Number(r.ad_spend || 0);
                      gst += Number(r.ad_spend_gst || 0);
                      revenue += Number(r.total_revenue || 0);
                    }
                  }
                  channelAgg[adCh] = { ad_channel: adCh, spend, gst, revenue };
                }
                const rows = d2cAdChannels.map(adCh => {
                  const a = channelAgg[adCh];
                  const roi = a.gst > 0 && a.revenue > 0 ? a.revenue / a.gst : 0;
                  const acos = a.revenue > 0 ? (a.gst / a.revenue) * 100 : 0;
                  return { ...a, roi, acos };
                });
                const tSpend = rows.reduce((s, r) => s + r.spend, 0);
                const tGst = rows.reduce((s, r) => s + r.gst, 0);
                const tRev = rows.reduce((s, r) => s + r.revenue, 0);
                const tRoi = tGst > 0 && tRev > 0 ? tRev / tGst : 0;
                const tAcos = tRev > 0 ? (tGst / tRev) * 100 : 0;
                return (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="d2c-channel-summary">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                      <h3 className="text-sm font-semibold text-[#0F0F0F]">D2C</h3>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{rows.length} ad channels</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: '240px' }} />
                          <col style={{ width: '170px' }} />
                          <col style={{ width: '160px' }} />
                          <col style={{ width: '90px' }} />
                          <col style={{ width: '100px' }} />
                          <col style={{ width: '28px' }} />
                        </colgroup>
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50/80">
                            <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Ad Channel</th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Spends (Incl. GST)</th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Revenue</th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ROI</th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ACOS</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => (
                            <tr key={r.ad_channel}
                              className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors cursor-pointer"
                              onClick={() => setDrillD2CAdChannel(r.ad_channel)}
                              data-testid={`d2c-row-${r.ad_channel.replace(/\s+/g, '-').toLowerCase()}`}>
                              <td className="py-2.5 px-4 font-medium text-[#0F0F0F]">
                                <div className="flex items-center gap-1.5"><span>{r.ad_channel}</span><ChevronRight className="w-3.5 h-3.5 text-gray-400" /></div>
                              </td>
                              <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(r.gst, 'currency')}</td>
                              <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(r.revenue, 'currency')}</td>
                              <td className="text-right py-2.5 px-3 font-medium tabular-nums whitespace-nowrap"><span className={r.roi >= 1 ? 'text-green-600' : r.roi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{r.roi.toFixed(2)}x</span></td>
                              <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{r.acos.toFixed(2)}%</td>
                              <td className="py-2.5 pr-2"><ChevronRight className="w-3.5 h-3.5 text-gray-300" /></td>
                            </tr>
                          ))}
                          {rows.length > 1 && (
                            <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                              <td className="text-left py-2.5 px-4 text-[#0F0F0F]">Total</td>
                              <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(tGst, 'currency')}</td>
                              <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(tRev, 'currency')}</td>
                              <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap"><span className={tRoi >= 1 ? 'text-green-600' : tRoi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{tRoi.toFixed(2)}x</span></td>
                              <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{tAcos.toFixed(2)}%</td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Back-to-channels link when drilled into a D2C ad-channel. */}
              {isUnlocked && isD2C && !drillProduct && drillD2CAdChannel && (
                <button onClick={() => setDrillD2CAdChannel(null)} className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm transition-colors" data-testid="d2c-back-btn">
                  <ArrowLeft className="w-4 h-4" /> Back to D2C ad channels
                </button>
              )}

              {/* ===== Blinkit — wrapper "On-Platform Ads" summary table =====
                  Renders ONE summary table with Performance & Reach as drillable
                  rows. Clicking a row sets `drillBlinkitAdType` which then
                  reveals just that ad-type's product table below. */}
              {isUnlocked && isBlinkit && !drillProduct && !drillBlinkitAdType && (() => {
                const blinkitAdTypes = Object.keys(channelData);
                const adTypeAgg = {};
                for (const at of blinkitAdTypes) {
                  const productsInType = channelData[at] || [];
                  let spend = 0, gst = 0, revenue = 0;
                  for (const p of productsInType) {
                    const rows = filteredAdData[p] || [];
                    for (const r of rows) {
                      spend += Number(r.ad_spend || 0);
                      gst += Number(r.ad_spend_gst || 0);
                      revenue += Number(r.total_revenue || 0);
                    }
                  }
                  adTypeAgg[at] = { ad_type: at, spend, gst, revenue };
                }
                const rows = blinkitAdTypes.map(at => {
                  const a = adTypeAgg[at];
                  const roi = a.gst > 0 && a.revenue > 0 ? a.revenue / a.gst : 0;
                  const acos = a.revenue > 0 ? (a.gst / a.revenue) * 100 : 0;
                  return { ...a, roi, acos };
                });
                const tSpend = rows.reduce((s, r) => s + r.spend, 0);
                const tGst = rows.reduce((s, r) => s + r.gst, 0);
                const tRev = rows.reduce((s, r) => s + r.revenue, 0);
                const tRoi = tGst > 0 && tRev > 0 ? tRev / tGst : 0;
                const tAcos = tRev > 0 ? (tGst / tRev) * 100 : 0;
                return (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="blinkit-onplatform-summary">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                      <h3 className="text-sm font-semibold text-[#0F0F0F]">On-Platform Ads</h3>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{rows.length} ad types</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: '240px' }} />
                          <col style={{ width: '170px' }} />
                          <col style={{ width: '160px' }} />
                          <col style={{ width: '90px' }} />
                          <col style={{ width: '100px' }} />
                          <col style={{ width: '28px' }} />
                        </colgroup>
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50/80">
                            <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Ad Type</th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Spends (Incl. GST)</th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Revenue</th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ROI</th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ACOS</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => (
                            <tr key={r.ad_type}
                              className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors cursor-pointer"
                              onClick={() => setDrillBlinkitAdType(r.ad_type)}
                              data-testid={`blinkit-row-${r.ad_type.replace(/\s+/g, '-').toLowerCase()}`}>
                              <td className="py-2.5 px-4 font-medium text-[#0F0F0F]">
                                <div className="flex items-center gap-1.5"><span>{r.ad_type}</span><ChevronRight className="w-3.5 h-3.5 text-gray-400" /></div>
                              </td>
                              <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(r.gst, 'currency')}</td>
                              <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(r.revenue, 'currency')}</td>
                              <td className="text-right py-2.5 px-3 font-medium tabular-nums whitespace-nowrap"><span className={r.roi >= 1 ? 'text-green-600' : r.roi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{r.roi.toFixed(2)}x</span></td>
                              <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{r.acos.toFixed(2)}%</td>
                              <td className="py-2.5 pr-2"><ChevronRight className="w-3.5 h-3.5 text-gray-300" /></td>
                            </tr>
                          ))}
                          {rows.length > 1 && (
                            <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                              <td className="text-left py-2.5 px-4 text-[#0F0F0F]">Total</td>
                              <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(tGst, 'currency')}</td>
                              <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(tRev, 'currency')}</td>
                              <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap"><span className={tRoi >= 1 ? 'text-green-600' : tRoi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{tRoi.toFixed(2)}x</span></td>
                              <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{tAcos.toFixed(2)}%</td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Back link when drilled into a Blinkit ad-type (Performance / Reach). */}
              {isUnlocked && isBlinkit && !drillProduct && drillBlinkitAdType && (
                <button onClick={() => setDrillBlinkitAdType(null)} className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm transition-colors" data-testid="blinkit-back-btn">
                  <ArrowLeft className="w-4 h-4" /> Back to On-Platform Ads
                </button>
              )}

              {/* ===== SUMMARY TABLES (default view) — grouped by Ad Type =====
                  For D2C, only render once a specific ad-channel is picked.
                  For Blinkit, only render once Performance or Reach is picked
                  (the wrapper "On-Platform Ads" table handles the default view). */}
              {isUnlocked && !drillProduct && (!isD2C || drillD2CAdChannel) && (!isBlinkit || drillBlinkitAdType) && adTypes.map(adTypeName => {
                // CHANGE 2: always show every property — never hide based on mode.
                const propsInType = (channelData[adTypeName] || []);
                if (propsInType.length === 0) return null;

                // Off-Platform Ads uses a different column shape (Spends · Spends GST · Post Type · No. of Posts).
                // Click → setDrillProduct(...) opens the dedicated drill view further down (Flipkart pattern).
                if (adTypeName === 'Off-Platform Ads') {
                  // Same 9-column Flipkart-Ads shape (Property · Preview · Spends · Spends GST ·
                  // Revenue · ROI · ACOS · %Spends Alloc · %Revenue Alloc). Click → standard
                  // campaign drill (back button + metric cards + campaign table).

                  // Visibility gate: Meta Ads by FK and Criteo by FK only exist from May 2026
                  // onward. Hide them entirely when the selected range ends before 2026-05-01.
                  const rangeEndsBeforeMay2026 = endDate && endDate < '2026-05-01';
                  const propsVisible = rangeEndsBeforeMay2026
                    ? propsInType.filter(p => !POST_MAY_2026_PRODUCTS.has(p))
                    : propsInType;

                  const opRows = propsVisible.map(prop => {
                    const rows = filteredAdData[prop] || [];
                    const spend = rows.reduce((s, r) => s + Number(r.ad_spend || 0), 0);
                    // Use override if present; else sum source revenue (0 for Telegram/Google).
                    const overrideKey = `${prop}|${startDate}|${endDate}`;
                    const override = revenueOverrides[overrideKey];
                    const sales = override !== undefined
                      ? override
                      : rows.reduce((s, r) => s + Number(r.total_revenue || 0), 0);
                    const gstSpend = spend * 1.18;
                    const roi = gstSpend > 0 && sales > 0 ? sales / gstSpend : 0;
                    const acos = sales > 0 ? (gstSpend / sales) * 100 : 0;
                    return { name: prop, spends: spend, sales, roi, acos, count: rows.length, overrideKey, isOverridden: override !== undefined };
                  });
                  const opSpends = opRows.reduce((s, r) => s + r.spends, 0);
                  const opSales = opRows.reduce((s, r) => s + r.sales, 0);
                  const opGst = opSpends * 1.18;
                  const opRoi = opGst > 0 && opSales > 0 ? opSales / opGst : 0;
                  const opAcos = opSales > 0 ? (opGst / opSales) * 100 : 0;

                  return (
                    <div key={adTypeName} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-visible" data-testid="off-platform-ads-summary">
                      <div className="px-5 py-4 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-[#0F0F0F]">{adTypeName}</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={{ width: '210px' }} />
                            <col style={{ width: '80px' }} />
                            <col style={{ width: '150px' }} />
                            <col style={{ width: '160px' }} />
                            <col style={{ width: '80px' }} />
                            <col style={{ width: '90px' }} />
                            <col style={{ width: '150px' }} />
                            <col style={{ width: '150px' }} />
                            <col style={{ width: '24px' }} />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/80">
                              <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Property</th>
                              <th className="text-center py-2.5 px-2 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Preview</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Spends (Incl. GST)</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Revenue</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">ROI</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">ACOS</th>
                              <th className="text-right py-2.5 pl-4 pr-3 font-semibold text-gray-500 text-xs uppercase tracking-wider border-l-2 border-blue-200 bg-blue-50/60 whitespace-nowrap align-middle"><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="spends_alloc" /><span>% Spends alloc.</span></span></th>
                              <th className="text-right py-2.5 pl-4 pr-3 font-semibold text-gray-500 text-xs uppercase tracking-wider bg-blue-50/60 whitespace-nowrap align-middle"><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="sales_alloc" /><span>% Revenue alloc.</span></span></th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {opRows.sort((a, b) => b.spends - a.spends).map((row) => {
                              const sa = opSpends > 0 ? ((row.spends / opSpends) * 100) : 0;
                              const sla = opSales > 0 ? ((row.sales / opSales) * 100) : 0;
                              const drillable = row.count > 0;
                              const supportsDaily = !monthlyOnlyProps.has(row.name);
                              const showBanner = isSingleDay && !supportsDaily;
                              return (
                                <React.Fragment key={row.name}>
                                <tr
                                  className={`border-b border-gray-100 hover:bg-gray-50/60 transition-colors ${drillable && !showBanner ? 'cursor-pointer' : ''}`}
                                  onClick={() => drillable && !showBanner && setDrillProduct(row.name)}
                                  data-testid={`off-platform-row-${row.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                  <td className="py-2.5 px-4 font-medium text-[#0F0F0F] align-middle" title={row.name}>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <MetricTooltip label={row.name} alwaysShow />
                                      {(() => {
                                        // Off-Platform rows are named like "Meta Ads by FK", "Google Ads",
                                        // "Criteo by FK". Try to find a platform icon by exact name first,
                                        // then by the first token (so "Meta Ads by FK" → Meta).
                                        const url = platformIcon(row.name) || platformIcon(row.name.split(' ')[0]);
                                        return url ? <img src={url} alt="" className="w-4 h-4 rounded object-contain shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null;
                                      })()}
                                      <span className="break-words">{row.name}</span>
                                      {availabilityByProduct[row.name]?.availability_type && <AvailabilityPill type={availabilityByProduct[row.name].availability_type} size="xs" />}
                                      {drillable && !showBanner && <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                                    </div>
                                  </td>
                                  <td className="text-center py-2.5 px-2">
                                    {getAdPreview(selectedChannel, row.name) && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setPreviewProduct(AD_PREVIEWS[`${selectedChannel}:${row.name}`] ? `${selectedChannel}:${row.name}` : row.name); }}
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[#32B4AC]/10 transition-colors group"
                                        data-testid={`preview-btn-${row.name.replace(/\s+/g, '-').toLowerCase()}`}
                                        title={`Preview ${row.name}`}
                                      >
                                        <Eye className="w-4 h-4 text-gray-400 group-hover:text-[#32B4AC] transition-colors" />
                                      </button>
                                    )}
                                  </td>
                                  {showBanner ? (
                                    <>
                                      <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums whitespace-nowrap">—</td>
                                      <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums whitespace-nowrap">—</td>
                                      <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums whitespace-nowrap">—</td>
                                      <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums whitespace-nowrap">—</td>
                                      <td className="text-right py-2.5 pl-4 pr-3 text-gray-400 tabular-nums border-l-2 border-blue-200 bg-blue-50/30 whitespace-nowrap">—</td>
                                      <td className="text-right py-2.5 pl-4 pr-3 text-gray-400 tabular-nums bg-blue-50/30 whitespace-nowrap">—</td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(row.spends * 1.18, 'currency')}</td>
                                      <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                        <EditableRevenue
                                          value={row.sales}
                                          isOverridden={row.isOverridden}
                                          onSave={(v) => saveRevenueOverride(row.overrideKey, v)}
                                          onClear={row.isOverridden ? () => clearRevenueOverride(row.overrideKey) : null}
                                        />
                                      </td>
                                      <td className="text-right py-2.5 px-3 font-medium tabular-nums whitespace-nowrap">
                                        <span className={row.roi >= 1 ? 'text-green-600' : row.roi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{(row.roi || 0).toFixed(2)}x</span>
                                      </td>
                                      <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{(row.acos || 0).toFixed(2)}%</td>
                                      <td className="text-right py-2.5 pl-4 pr-3 font-medium text-blue-700 tabular-nums border-l-2 border-blue-200 bg-blue-50/30 whitespace-nowrap">{sa.toFixed(2)}%</td>
                                      <td className="text-right py-2.5 pl-4 pr-3 font-medium text-blue-700 tabular-nums bg-blue-50/30 whitespace-nowrap">{sla.toFixed(2)}%</td>
                                    </>
                                  )}
                                  <td className="py-2.5 pr-2">{drillable && !showBanner && <ChevronRight className="w-3.5 h-3.5 text-gray-300" />}</td>
                                </tr>
                                {showBanner && (
                                  <tr data-testid={`daily-unavailable-banner-${row.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                    <td colSpan={9} style={{ borderTop: '2px solid #E24B4A', backgroundColor: '#FCEBEB' }} className="px-4 py-2">
                                      <div className="flex items-center gap-2" style={{ color: '#A32D2D', fontSize: 11.5 }}>
                                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                        <span>Date-wise metrics not available for this property</span>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                </React.Fragment>
                              );
                            })}
                            {opRows.length > 1 && (
                              <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                                <td className="text-left py-2.5 px-4 text-[#0F0F0F] whitespace-nowrap">Total</td>
                                <td></td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(opGst, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(opSales, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap"><span className={opRoi >= 1 ? 'text-green-600' : opRoi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{opRoi.toFixed(2)}x</span></td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{opAcos.toFixed(2)}%</td>
                                <td className="text-right py-2.5 pl-4 pr-3 font-bold text-blue-800 tabular-nums border-l-2 border-blue-200 bg-blue-50/30 whitespace-nowrap">100.00%</td>
                                <td className="text-right py-2.5 pl-4 pr-3 font-bold text-blue-800 tabular-nums bg-blue-50/30 whitespace-nowrap">100.00%</td>
                                <td></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                }
                const typeRows = propsInType.map(prop => {
                  const rows = filteredAdData[prop] || [];
                  const spend = rows.reduce((s, r) => s + (r.ad_spend || 0), 0);
                  const sales = rows.reduce((s, r) => s + (r.total_revenue || 0), 0);
                  const gstSpend = spend * 1.18;
                  const roi = gstSpend > 0 ? sales / gstSpend : 0;
                  const acos = sales > 0 ? (gstSpend / sales) * 100 : 0;
                  return { name: prop, spends: spend, sales, roi, acos, count: rows.length };
                });
                const typeSpends = typeRows.reduce((s, r) => s + r.spends, 0);
                const typeSales = typeRows.reduce((s, r) => s + r.sales, 0);

                return (
                  <div key={adTypeName} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-visible">
                    <div className="px-5 py-4 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-[#0F0F0F]">{adTypeName}</h3>
                    </div>
                    {/* Notification for properties without daily data */}
                    {drillDate && (() => {
                      const noDailyProps = propsInType.filter(prop => {
                        const allRows = adData[prop] || [];
                        const dayRows = filteredAdData[prop] || [];
                        // Property has data overall but none for this specific day
                        return allRows.length > 0 && dayRows.length === 0;
                      });
                      if (noDailyProps.length === 0) return null;
                      return (
                        <div className="mx-4 mt-3 mb-1 px-3 py-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="no-daily-data-warning">
                          <span className="text-red-500 text-sm mt-0.5">!</span>
                          <div className="text-xs text-red-600">
                            <span className="font-semibold">Daily-level reporting is not available for {noDailyProps.length === 1 ? `"${noDailyProps[0]}"` : noDailyProps.map(p => `"${p}"`).join(', ')} at present</span>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" style={{ tableLayout: 'fixed' }} data-testid={`ad-summary-${adTypeName.replace(/\s+/g, '-').toLowerCase()}`}>
                        {/* Locked column widths — guarantees header + body + total row never shift on data update */}
                        <colgroup>
                          <col style={{ width: '210px' }} />{/* Property */}
                          <col style={{ width: '80px' }} />{/* Preview */}
                          <col style={{ width: '150px' }} />{/* Spends (Incl. GST) */}
                          <col style={{ width: '140px' }} />{/* Revenue */}
                          <col style={{ width: '80px' }} />{/* ROI */}
                          <col style={{ width: '90px' }} />{/* ACOS */}
                          <col style={{ width: '150px' }} />{/* % Spends Alloc. */}
                          <col style={{ width: '150px' }} />{/* % Revenue Alloc. */}
                          <col style={{ width: '24px' }} />{/* chevron */}
                        </colgroup>
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50/80">
                            <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Property</th>
                            <th className="text-center py-2.5 px-2 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Preview</th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap align-middle" onClick={() => toggleSort('spends', setSortConfig, sortConfig)}><span className="inline-flex items-center justify-end gap-1.5 w-full"><MetricTooltip label="spends" /><span>Spends (Incl. GST)</span><SortIcon colKey="spends" config={sortConfig} /></span></th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap align-middle" onClick={() => toggleSort('sales', setSortConfig, sortConfig)}><span className="inline-flex items-center justify-end gap-1.5 w-full"><MetricTooltip label="revenue" /><span>Revenue</span><SortIcon colKey="sales" config={sortConfig} /></span></th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap align-middle" onClick={() => toggleSort('roi', setSortConfig, sortConfig)}><span className="inline-flex items-center justify-end gap-1.5 w-full"><MetricTooltip label="roi" /><span>ROI</span><SortIcon colKey="roi" config={sortConfig} /></span></th>
                            <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap align-middle" onClick={() => toggleSort('acos', setSortConfig, sortConfig)}><span className="inline-flex items-center justify-end gap-1.5 w-full"><MetricTooltip label="acos" /><span>ACOS</span><SortIcon colKey="acos" config={sortConfig} /></span></th>
                            <th className="text-right py-2.5 pl-4 pr-3 font-semibold text-gray-500 text-xs uppercase tracking-wider border-l-2 border-blue-200 bg-blue-50/60 whitespace-nowrap align-middle"><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="spends_alloc" /><span>% Spends alloc.</span></span></th>
                            <th className="text-right py-2.5 pl-4 pr-3 font-semibold text-gray-500 text-xs uppercase tracking-wider bg-blue-50/60 whitespace-nowrap align-middle"><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="sales_alloc" /><span>% Revenue alloc.</span></span></th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {applySummarySort(typeRows).map((row) => {
                            const sa = typeSpends > 0 ? ((row.spends / typeSpends) * 100) : 0;
                            const sla = typeSales > 0 ? ((row.sales / typeSales) * 100) : 0;
                            const hasRows = row.count > 0;
                            const hasPreview = !!getAdPreview(selectedChannel, row.name);
                            const supportsDaily = !monthlyOnlyProps.has(row.name);
                            const showBanner = isSingleDay && !supportsDaily;
                            return (
                              <React.Fragment key={row.name}>
                              <tr
                                className={`border-b border-gray-100 hover:bg-gray-50/60 transition-colors ${hasRows ? 'cursor-pointer' : ''}`}
                                onClick={() => hasRows && setDrillProduct(row.name)}
                                data-testid={`ad-row-${row.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                <td className="py-2.5 px-4 font-medium text-[#0F0F0F] align-middle" title={row.name}>
                                  <div className="flex items-center gap-1.5 flex-wrap"><MetricTooltip label={row.name} alwaysShow />{(() => { const url = platformIcon(row.name) || platformIcon(row.name.split(' ')[0]); return url ? <img src={url} alt="" className="w-4 h-4 rounded object-contain shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null; })()}<span className="break-words">{row.name}</span>{availabilityByProduct[row.name]?.availability_type && <AvailabilityPill type={availabilityByProduct[row.name].availability_type} size="xs" />}{hasRows && <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}</div>
                                </td>
                                <td className="text-center py-2.5 px-2">
                                  {hasPreview && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setPreviewProduct(AD_PREVIEWS[`${selectedChannel}:${row.name}`] ? `${selectedChannel}:${row.name}` : row.name); }}
                                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[#32B4AC]/10 transition-colors group"
                                      data-testid={`preview-btn-${row.name.replace(/\s+/g, '-').toLowerCase()}`}
                                      title={`View ${row.name} ad placements`}
                                    >
                                      <Eye className="w-4 h-4 text-gray-400 group-hover:text-[#32B4AC] transition-colors" />
                                    </button>
                                  )}
                                </td>
                                {showBanner ? (
                                  <>
                                    <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums whitespace-nowrap">—</td>
                                    <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums whitespace-nowrap">—</td>
                                    <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums whitespace-nowrap">—</td>
                                    <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums whitespace-nowrap">—</td>
                                    <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums border-l-2 border-blue-200 bg-blue-50/30 whitespace-nowrap">—</td>
                                    <td className="text-right py-2.5 px-3 text-gray-400 tabular-nums bg-blue-50/30 whitespace-nowrap">—</td>
                                  </>
                                ) : (
                                  <>
                                    <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(row.spends * 1.18, 'currency')}</td>
                                    <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(row.sales, 'currency')}</td>
                                    <td className="text-right py-2.5 px-3 font-medium tabular-nums whitespace-nowrap">
                                      <span className={row.roi >= 1 ? 'text-green-600' : row.roi >= 0 ? 'text-yellow-600' : 'text-red-600'}>{(row.roi || 0).toFixed(2)}x</span>
                                    </td>
                                    <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{(row.acos || 0).toFixed(2)}%</td>
                                    <td className="text-right py-2.5 px-3 font-medium text-blue-700 tabular-nums border-l-2 border-blue-200 bg-blue-50/30 whitespace-nowrap">{sa.toFixed(2)}%</td>
                                    <td className="text-right py-2.5 px-3 font-medium text-blue-700 tabular-nums bg-blue-50/30 whitespace-nowrap">{sla.toFixed(2)}%</td>
                                  </>
                                )}
                                <td className="py-2.5 pr-2">{hasRows && !showBanner && <ChevronRight className="w-3.5 h-3.5 text-gray-300" />}</td>
                              </tr>
                              {showBanner && (
                                <tr data-testid={`daily-unavailable-banner-${row.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                  <td colSpan={9} style={{ borderTop: '2px solid #E24B4A', backgroundColor: '#FCEBEB' }} className="px-4 py-2">
                                    <div className="flex items-center gap-2" style={{ color: '#A32D2D', fontSize: 11.5 }}>
                                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                      <span>Date-wise metrics not available for this property</span>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            );
                          })}
                          {typeRows.length > 1 && (() => {
                            const typeGstSpends = typeSpends * 1.18;
                            const tRoi = typeGstSpends > 0 ? typeSales / typeGstSpends : 0;
                            const tAcos = typeSales > 0 ? (typeGstSpends / typeSales) * 100 : 0;
                            return (
                              <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                                <td className="text-left py-2.5 px-4 text-[#0F0F0F] whitespace-nowrap">Total</td>
                                <td></td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(typeSpends * 1.18, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(typeSales, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap"><span className={tRoi >= 1 ? 'text-green-600' : 'text-yellow-600'}>{tRoi.toFixed(2)}x</span></td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{tAcos.toFixed(2)}%</td>
                                <td className="text-right py-2.5 px-3 font-bold text-blue-800 tabular-nums border-l-2 border-blue-200 bg-blue-50/30 whitespace-nowrap">100.00%</td>
                                <td className="text-right py-2.5 px-3 font-bold text-blue-800 tabular-nums bg-blue-50/30 whitespace-nowrap">100.00%</td>
                                <td></td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}


              {/* ===== TELEGRAM ADS CASCADING DRILL (Off-Platform Ads) ===== */}
              {isUnlocked && drillProduct === 'Telegram Ads' && (() => {
                // Filter the Telegram rows by active drill path
                const allRows = filteredAdData['Telegram Ads'] || [];
                let scoped = allRows;
                if (drillTgGroup) scoped = scoped.filter(r => (r.telegram_group || '').trim() === drillTgGroup);
                if (drillTgBrand) scoped = scoped.filter(r => (r.brand || '').trim() === drillTgBrand);

                // Aggregator helpers
                const aggBy = (rows, field) => {
                  const m = new Map();
                  for (const r of rows) {
                    const k = (r[field] || '—').toString().trim() || '—';
                    if (!m.has(k)) m.set(k, { key: k, spends: 0, spends_gst: 0, nop: 0, post_types: new Set() });
                    const b = m.get(k);
                    b.spends += Number(r.ad_spend || 0);
                    b.spends_gst += Number(r.ad_spend_gst || 0);
                    b.nop += Number(r.nop || 0);
                    if (r.post_type) b.post_types.add(r.post_type);
                  }
                  return Array.from(m.values()).sort((a, b) => b.spends - a.spends);
                };
                const aggSku = (rows) => {
                  const m = new Map();
                  for (const r of rows) {
                    const k = (r.sku_id || '—').trim();
                    if (!m.has(k)) m.set(k, { key: k, sku_id: r.sku_id || '—', sku_name: r.sku_name || '', spends: 0, spends_gst: 0, nop: 0, post_types: new Set() });
                    const b = m.get(k);
                    b.spends += Number(r.ad_spend || 0);
                    b.spends_gst += Number(r.ad_spend_gst || 0);
                    b.nop += Number(r.nop || 0);
                    if (r.post_type) b.post_types.add(r.post_type);
                  }
                  return Array.from(m.values()).sort((a, b) => b.spends - a.spends);
                };
                const ptLabel = (s) => s.size === 0 ? '—' : (s.size === 1 ? Array.from(s)[0] : `${s.size} types`);

                // Decide what to render based on cascade depth
                let level, backLabel, backFn, headerLabel, propertyHeader, rows, countLabel;
                if (!drillTgGroup) {
                  level = 1;
                  backLabel = 'Back to summary';
                  backFn = () => setDrillProduct(null);
                  headerLabel = 'Telegram Ads';
                  propertyHeader = 'Telegram Group';
                  rows = aggBy(scoped, 'telegram_group');
                  countLabel = `${rows.length} group${rows.length === 1 ? '' : 's'}`;
                } else if (!drillTgBrand) {
                  level = 2;
                  backLabel = 'Back to Telegram Ads';
                  backFn = () => setDrillTgGroup(null);
                  headerLabel = drillTgGroup;
                  propertyHeader = 'Brand';
                  rows = aggBy(scoped, 'brand');
                  countLabel = `${rows.length} brand${rows.length === 1 ? '' : 's'}`;
                } else {
                  level = 3;
                  backLabel = `Back to ${drillTgGroup}`;
                  backFn = () => setDrillTgBrand(null);
                  headerLabel = drillTgBrand;
                  propertyHeader = 'SKU';
                  rows = aggSku(scoped);
                  countLabel = `${rows.length} SKU${rows.length === 1 ? '' : 's'}`;
                }
                const isLeaf = level === 3;

                // Breadcrumb chips
                const crumbs = [{ label: 'All Properties', onClick: () => setDrillProduct(null) }];
                if (drillTgGroup) crumbs.push({ label: drillTgGroup, onClick: () => setDrillTgGroup(null) });
                if (drillTgBrand) crumbs.push({ label: drillTgBrand, onClick: () => setDrillTgBrand(null) });

                // Scoped metric totals — Off-Platform shape (incl. editable Revenue + ROI/ACOS)
                const sSpends = scoped.reduce((s, r) => s + Number(r.ad_spend || 0), 0);
                const sSpendsGst = scoped.reduce((s, r) => s + Number(r.ad_spend_gst || 0), 0);
                const sPosts = scoped.reduce((s, r) => s + Number(r.nop || 0), 0);
                // Revenue: use override scoped to (Telegram Ads + group? + brand? + date range)
                const ovKey = ['Telegram Ads', drillTgGroup || '', drillTgBrand || '', startDate, endDate].join('|');
                const ovValue = revenueOverrides[ovKey];
                const sSales = ovValue !== undefined ? ovValue : 0;
                const sRoi = sSpendsGst > 0 && sSales > 0 ? sSales / sSpendsGst : 0;
                const sAcos = sSales > 0 ? (sSpendsGst / sSales) * 100 : 0;

                const drillNext = (row) => {
                  if (level === 1) setDrillTgGroup(row.key);
                  else if (level === 2) setDrillTgBrand(row.key);
                };

                return (
                  <div className="space-y-3" data-testid={`tg-drill-level-${level}`}>
                    <button onClick={backFn} className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm transition-colors" data-testid="tg-drill-back">
                      <ArrowLeft className="w-4 h-4" /> {backLabel}
                    </button>

                    {/* Scoped metric cards — Off-Platform shape */}
                    <div className="grid grid-cols-5 gap-3">
                      {[
                        { label: 'SPENDS (INCL. GST)', value: fmtVal(sSpendsGst, 'currency') },
                        { label: 'REVENUE', editable: true },
                        { label: 'ROI', value: `${sRoi.toFixed(2)}x` },
                        { label: 'ACOS', value: `${sAcos.toFixed(2)}%` },
                        { label: 'POSTS', value: Number(sPosts).toLocaleString('en-IN') },
                      ].map((m) => (
                        <div key={m.label} className="bg-white border border-gray-200 rounded-xl px-3 py-2.5">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{m.label}</div>
                          {m.editable ? (
                            <div className="text-base font-bold tabular-nums text-[#0F0F0F] mt-1">
                              <EditableRevenue
                                value={sSales}
                                isOverridden={ovValue !== undefined}
                                onSave={(v) => saveRevenueOverride(ovKey, v)}
                                onClear={ovValue !== undefined ? () => clearRevenueOverride(ovKey) : null}
                              />
                            </div>
                          ) : (
                            <div className="text-base font-bold tabular-nums text-[#0F0F0F] mt-1">{m.value}</div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
                        <h3 className="text-sm font-semibold text-[#0F0F0F]">{headerLabel}</h3>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{countLabel}</span>
                      </div>
                      {/* Breadcrumb strip */}
                      <div className="px-5 py-2 border-b border-gray-100 bg-slate-50/40 flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
                        {crumbs.map((c, i) => (
                          <span key={i} className="flex items-center gap-1.5">
                            {i > 0 && <ChevronRight className="w-3 h-3 text-gray-300" />}
                            {i === crumbs.length - 1 ? (
                              <span className="font-medium text-[#0F0F0F] truncate max-w-[200px]" title={c.label}>{c.label}</span>
                            ) : (
                              <button onClick={c.onClick} className="hover:text-[#37b6ad] hover:underline transition-colors truncate max-w-[160px]" title={c.label}>{c.label}</button>
                            )}
                          </span>
                        ))}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={{ width: '280px' }} />
                            <col style={{ width: '170px' }} />
                            <col style={{ width: '140px' }} />
                            <col style={{ width: '120px' }} />
                            <col style={{ width: '24px' }} />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/80">
                              <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">{propertyHeader}</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Spends (Incl. GST)</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Post Type</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">No. of Posts</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.length === 0 && (
                              <tr><td colSpan={5} className="py-8 text-center text-gray-400 text-sm">No data available</td></tr>
                            )}
                            {rows.map((row) => (
                              <tr key={row.key}
                                className={`border-b border-gray-100 hover:bg-gray-50/60 transition-colors ${!isLeaf ? 'cursor-pointer' : ''}`}
                                onClick={() => !isLeaf && drillNext(row)}
                                data-testid={`tg-drill-row-${level}-${row.key.replace(/\s+/g, '-').toLowerCase()}`}>
                                <td className="py-2.5 px-4 font-medium text-[#0F0F0F] align-middle" title={isLeaf ? `${row.sku_id} — ${row.sku_name}` : row.key}>
                                  {isLeaf ? (
                                    <div className="min-w-0">
                                      <div className="truncate text-[#0F0F0F]">{row.sku_id}</div>
                                      {row.sku_name && <div className="text-[11px] text-gray-400 truncate">{row.sku_name}</div>}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1.5">
                                      <span className="break-words">{row.key}</span>
                                      <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    </div>
                                  )}
                                </td>
                                <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(row.spends_gst, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 text-gray-600 whitespace-nowrap">{ptLabel(row.post_types)}</td>
                                <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{Number(row.nop).toLocaleString('en-IN')}</td>
                                <td className="py-2.5 pr-2">{!isLeaf && <ChevronRight className="w-3.5 h-3.5 text-gray-300" />}</td>
                              </tr>
                            ))}
                            {rows.length > 1 && (
                              <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                                <td className="text-left py-2.5 px-4 text-[#0F0F0F] whitespace-nowrap">Total</td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(sSpendsGst, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 text-gray-600 whitespace-nowrap">—</td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{Number(sPosts).toLocaleString('en-IN')}</td>
                                <td></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ===== GOOGLE ADS CASCADING DRILL (Campaign → Brand → Product) ===== */}
              {isUnlocked && drillProduct === 'Google Ads' && (() => {
                // Force-normalize legacy data: every Google Ads row rolls up under a single campaign name.
                const GOOGLE_ADS_CAMPAIGN_NAME = 'Wellcore_Crtn_GGL';
                const allRowsRaw = filteredAdData['Google Ads'] || [];
                const allRows = allRowsRaw.map(r => ({ ...r, campaign_name: GOOGLE_ADS_CAMPAIGN_NAME }));
                let scoped = allRows;
                if (drillGoogleCampaign) scoped = scoped.filter(r => (r.campaign_name || '').trim() === drillGoogleCampaign);
                if (drillGoogleBrand) scoped = scoped.filter(r => (r.brand || '').trim() === drillGoogleBrand);

                // Aggregator by single dimension (for Campaign and Brand levels)
                const aggBy = (rows, field) => {
                  const m = new Map();
                  for (const r of rows) {
                    const k = (r[field] || '—').toString().trim() || '—';
                    if (!m.has(k)) m.set(k, { key: k, ad_spend: 0, views: 0, clicks: 0, units_sold: 0, total_revenue: 0 });
                    const b = m.get(k);
                    b.ad_spend += Number(r.ad_spend || 0);
                    b.views += Number(r.views || 0);
                    b.clicks += Number(r.clicks || 0);
                    b.units_sold += Number(r.units_sold || r.total_converted_units || 0);
                    b.total_revenue += Number(r.total_revenue || 0);
                  }
                  return Array.from(m.values()).map(b => ({
                    ...b,
                    ctr: b.views > 0 ? (b.clicks / b.views) * 100 : 0,
                    cvr: b.clicks > 0 ? (b.units_sold / b.clicks) * 100 : 0,
                    cpc: b.clicks > 0 ? b.ad_spend / b.clicks : 0,
                    roi: b.ad_spend > 0 ? b.total_revenue / b.ad_spend : 0,
                  })).sort((a, b) => b.ad_spend - a.ad_spend);
                };

                // Product-level (leaf): preserve raw rows + show product_title + item_id
                const aggProduct = (rows) => {
                  const m = new Map();
                  for (const r of rows) {
                    const itemId = (r.product_item_id || r.item_id || '—').toString().trim() || '—';
                    if (!m.has(itemId)) m.set(itemId, {
                      key: itemId, product_item_id: itemId, product_title: r.product_title || '—',
                      ad_spend: 0, views: 0, clicks: 0, units_sold: 0, total_revenue: 0,
                    });
                    const b = m.get(itemId);
                    b.ad_spend += Number(r.ad_spend || 0);
                    b.views += Number(r.views || 0);
                    b.clicks += Number(r.clicks || 0);
                    b.units_sold += Number(r.units_sold || r.total_converted_units || 0);
                    b.total_revenue += Number(r.total_revenue || 0);
                  }
                  return Array.from(m.values()).map(b => ({
                    ...b,
                    ctr: b.views > 0 ? (b.clicks / b.views) * 100 : 0,
                    cvr: b.clicks > 0 ? (b.units_sold / b.clicks) * 100 : 0,
                    cpc: b.clicks > 0 ? b.ad_spend / b.clicks : 0,
                    roi: b.ad_spend > 0 ? b.total_revenue / b.ad_spend : 0,
                  })).sort((a, b) => b.ad_spend - a.ad_spend);
                };

                // Choose level
                let level, backLabel, backFn, headerLabel, rows, countLabel, firstColKey, firstColLabel, secondColKey, secondColLabel;
                if (!drillGoogleCampaign) {
                  level = 1; backLabel = 'Back to summary'; backFn = () => setDrillProduct(null);
                  headerLabel = 'Google Ads'; rows = aggBy(scoped, 'campaign_name');
                  firstColKey = 'key'; firstColLabel = 'Campaign Name';
                  countLabel = `${rows.length} campaign${rows.length === 1 ? '' : 's'}`;
                } else if (!drillGoogleBrand) {
                  level = 2; backLabel = 'Back to Google Ads'; backFn = () => setDrillGoogleCampaign(null);
                  headerLabel = drillGoogleCampaign; rows = aggBy(scoped, 'brand');
                  firstColKey = 'key'; firstColLabel = 'Brand';
                  countLabel = `${rows.length} brand${rows.length === 1 ? '' : 's'}`;
                } else {
                  level = 3; backLabel = `Back to ${drillGoogleCampaign}`; backFn = () => setDrillGoogleBrand(null);
                  headerLabel = drillGoogleBrand; rows = aggProduct(scoped);
                  firstColKey = 'product_title'; firstColLabel = 'Product Title';
                  secondColKey = 'product_item_id'; secondColLabel = 'Item ID';
                  countLabel = `${rows.length} product${rows.length === 1 ? '' : 's'}`;
                }
                const isLeaf = level === 3;

                // Breadcrumbs
                const crumbs = [{ label: 'All Properties', onClick: () => setDrillProduct(null) }];
                if (drillGoogleCampaign) crumbs.push({ label: drillGoogleCampaign, onClick: () => setDrillGoogleCampaign(null) });
                if (drillGoogleBrand) crumbs.push({ label: drillGoogleBrand, onClick: () => setDrillGoogleBrand(null) });

                // Scoped totals — Off-Platform shape (editable Revenue + ROI/ACOS)
                const sSpends = scoped.reduce((s, r) => s + Number(r.ad_spend || 0), 0);
                const sSpendsGst = sSpends * 1.18;
                const sViews = scoped.reduce((s, r) => s + Number(r.views || 0), 0);
                const sClicks = scoped.reduce((s, r) => s + Number(r.clicks || 0), 0);
                const sUnits = scoped.reduce((s, r) => s + Number(r.units_sold || r.total_converted_units || 0), 0);
                const ovKey = ['Google Ads', drillGoogleCampaign || '', drillGoogleBrand || '', startDate, endDate].join('|');
                const ovValue = revenueOverrides[ovKey];
                const sourceRev = scoped.reduce((s, r) => s + Number(r.total_revenue || 0), 0);
                const sSales = ovValue !== undefined ? ovValue : sourceRev;
                const sRoi = sSpendsGst > 0 && sSales > 0 ? sSales / sSpendsGst : 0;
                const sAcos = sSales > 0 ? (sSpendsGst / sSales) * 100 : 0;

                const drillNext = (row) => {
                  if (level === 1) setDrillGoogleCampaign(row.key);
                  else if (level === 2) setDrillGoogleBrand(row.key);
                };

                // Totals
                const tSpend = rows.reduce((s, r) => s + (r.ad_spend || 0), 0);
                const tViews = rows.reduce((s, r) => s + (r.views || 0), 0);
                const tClicks = rows.reduce((s, r) => s + (r.clicks || 0), 0);
                const tUnits = rows.reduce((s, r) => s + (r.units_sold || 0), 0);
                const tRev = rows.reduce((s, r) => s + (r.total_revenue || 0), 0);
                const tCtr = tViews > 0 ? (tClicks / tViews) * 100 : 0;
                const tCvr = tClicks > 0 ? (tUnits / tClicks) * 100 : 0;
                const tCpc = tClicks > 0 ? tSpend / tClicks : 0;
                const tRoi = tSpend > 0 ? tRev / tSpend : 0;

                const metricCells = [
                  { key: 'ad_spend', label: 'Spends', fmt: 'currency', total: tSpend },
                  { key: 'views', label: 'Views', fmt: 'number', total: tViews },
                  { key: 'clicks', label: 'Clicks', fmt: 'number', total: tClicks },
                  { key: 'ctr', label: 'CTR', fmt: 'pct', total: tCtr },
                  { key: 'units_sold', label: 'Units Sold', fmt: 'number', total: tUnits },
                  { key: 'cvr', label: 'CVR', fmt: 'pct', total: tCvr },
                  { key: 'total_revenue', label: 'Sales', fmt: 'currency', total: tRev },
                  { key: 'cpc', label: 'CPC', fmt: 'currency', total: tCpc },
                  { key: 'roi', label: 'ROAS', fmt: 'decimal', total: tRoi },
                ];

                return (
                  <div className="space-y-3" data-testid={`google-drill-level-${level}`}>
                    <button onClick={backFn} className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm transition-colors" data-testid="google-drill-back">
                      <ArrowLeft className="w-4 h-4" /> {backLabel}
                    </button>

                    {/* Scoped metric cards — Off-Platform shape */}
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { label: 'SPENDS (INCL. GST)', value: fmtVal(sSpendsGst, 'currency') },
                        { label: 'REVENUE', editable: true },
                        { label: 'ROI', value: `${sRoi.toFixed(2)}x` },
                        { label: 'ACOS', value: `${sAcos.toFixed(2)}%` },
                      ].map((m) => (
                        <div key={m.label} className="bg-white border border-gray-200 rounded-xl px-3 py-2.5">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{m.label}</div>
                          {m.editable ? (
                            <div className="text-base font-bold tabular-nums text-[#0F0F0F] mt-1">
                              <EditableRevenue
                                value={sSales}
                                isOverridden={ovValue !== undefined}
                                onSave={(v) => saveRevenueOverride(ovKey, v)}
                                onClear={ovValue !== undefined ? () => clearRevenueOverride(ovKey) : null}
                              />
                            </div>
                          ) : (
                            <div className="text-base font-bold tabular-nums text-[#0F0F0F] mt-1">{m.value}</div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
                        <h3 className="text-sm font-semibold text-[#0F0F0F]">{headerLabel}</h3>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{countLabel}</span>
                      </div>
                      {/* Breadcrumb strip */}
                      <div className="px-5 py-2 border-b border-gray-100 bg-slate-50/40 flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
                        {crumbs.map((c, i) => (
                          <span key={i} className="flex items-center gap-1.5">
                            {i > 0 && <ChevronRight className="w-3 h-3 text-gray-300" />}
                            <button onClick={c.onClick} className="hover:text-[#32B4AC] hover:underline">{c.label}</button>
                          </span>
                        ))}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }} data-testid={`google-drill-table-${level}`}>
                          <colgroup>
                            <col style={{ width: isLeaf ? '280px' : '260px' }} />
                            {isLeaf && <col style={{ width: '140px' }} />}
                            {metricCells.map(m => <col key={m.key} style={{ width: m.fmt === 'currency' ? '120px' : (m.fmt === 'pct' || m.fmt === 'decimal' ? '90px' : '100px') }} />)}
                            {!isLeaf && <col style={{ width: '40px' }} />}
                          </colgroup>
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/80">
                              <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-[11px] uppercase tracking-wider whitespace-nowrap">{firstColLabel}</th>
                              {isLeaf && <th className="text-left py-2.5 px-3 font-semibold text-gray-500 text-[11px] uppercase tracking-wider whitespace-nowrap">{secondColLabel}</th>}
                              {metricCells.map(m => (
                                <th key={m.key} className="text-right py-2.5 px-3 font-semibold text-gray-500 text-[11px] uppercase tracking-wider whitespace-nowrap">{m.label}</th>
                              ))}
                              {!isLeaf && <th></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.slice(0, 200).map((row, idx) => (
                              <tr key={idx}
                                onClick={isLeaf ? undefined : () => drillNext(row)}
                                className={`border-b border-gray-50 hover:bg-gray-50/60 transition-colors ${isLeaf ? '' : 'cursor-pointer'}`}>
                                <td className="text-left py-2 px-4 text-[#0F0F0F] font-medium truncate" title={row[firstColKey]}>
                                  {row[firstColKey] || '—'}
                                </td>
                                {isLeaf && (
                                  <td className="text-left py-2 px-3 text-gray-600 tabular-nums truncate" title={row[secondColKey]}>
                                    {row[secondColKey] || '—'}
                                  </td>
                                )}
                                {metricCells.map(m => (
                                  <td key={m.key} className="text-right py-2 px-3 text-gray-700 tabular-nums whitespace-nowrap">
                                    {fmtVal(row[m.key], m.fmt)}
                                  </td>
                                ))}
                                {!isLeaf && (
                                  <td className="text-right py-2 px-3">
                                    <ChevronRight className="w-4 h-4 text-gray-300 inline" />
                                  </td>
                                )}
                              </tr>
                            ))}
                            {rows.length > 200 && (
                              <tr><td colSpan={metricCells.length + (isLeaf ? 2 : 2)} className="py-2 text-center text-xs text-gray-400">Showing top 200 of {rows.length}</td></tr>
                            )}
                            {/* Total row */}
                            <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                              <td className="text-left py-2.5 px-4 text-[#0F0F0F] whitespace-nowrap">Total ({rows.length})</td>
                              {isLeaf && <td className="text-left py-2.5 px-3 text-gray-400">—</td>}
                              {metricCells.map(m => (
                                <td key={m.key} className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">
                                  {fmtVal(m.total, m.fmt)}
                                </td>
                              ))}
                              {!isLeaf && <td></td>}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ===== 3P DSP CASCADING DRILL (Brand → Campaign) ===== */}
              {isUnlocked && drillProduct === '3P DSP' && (() => {
                const rawRows = filteredAdData['3P DSP'] || [];
                const brandAgg = {};
                for (const r of rawRows) {
                  const b = r.brand || 'Other';
                  if (!brandAgg[b]) brandAgg[b] = { brand: b, ad_spend: 0, total_revenue: 0 };
                  brandAgg[b].ad_spend += Number(r.ad_spend || 0);
                  brandAgg[b].total_revenue += Number(r.total_revenue || 0);
                }
                const brandRows = Object.values(brandAgg).map(b => {
                  const gst = b.ad_spend * 1.18;
                  return {
                    ...b,
                    ad_spend_gst: gst,
                    roi: gst > 0 && b.total_revenue > 0 ? b.total_revenue / gst : 0,
                    acos: b.total_revenue > 0 ? (gst / b.total_revenue) * 100 : 0,
                  };
                });
                const tSpend = brandRows.reduce((s, r) => s + r.ad_spend, 0);
                const tGst = tSpend * 1.18;
                const tRev = brandRows.reduce((s, r) => s + r.total_revenue, 0);
                const tRoi = tGst > 0 && tRev > 0 ? tRev / tGst : 0;
                const tAcos = tRev > 0 ? (tGst / tRev) * 100 : 0;

                if (drill3PDspBrand) {
                  const campMap = {};
                  for (const r of rawRows) {
                    if ((r.brand || 'Other') !== drill3PDspBrand) continue;
                    const k = r.campaign_name || 'Unknown';
                    if (!campMap[k]) campMap[k] = { campaign_name: k, ad_spend: 0, total_revenue: 0 };
                    campMap[k].ad_spend += Number(r.ad_spend || 0);
                    campMap[k].total_revenue += Number(r.total_revenue || 0);
                  }
                  const camps = Object.values(campMap).map(c => {
                    const gst = c.ad_spend * 1.18;
                    return {
                      ...c,
                      ad_spend_gst: gst,
                      roi: gst > 0 && c.total_revenue > 0 ? c.total_revenue / gst : 0,
                      acos: c.total_revenue > 0 ? (gst / c.total_revenue) * 100 : 0,
                    };
                  }).sort((a, b) => b.ad_spend - a.ad_spend);
                  const cSpend = camps.reduce((s, r) => s + r.ad_spend, 0);
                  const cGst = cSpend * 1.18;
                  const cRev = camps.reduce((s, r) => s + r.total_revenue, 0);
                  const cRoi = cGst > 0 && cRev > 0 ? cRev / cGst : 0;
                  const cAcos = cRev > 0 ? (cGst / cRev) * 100 : 0;
                  return (
                    <div className="space-y-3" data-testid="dsp-campaign-drill">
                      <button onClick={() => setDrill3PDspBrand(null)} className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Back to 3P DSP brands
                      </button>
                      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                          <h3 className="text-sm font-semibold text-[#0F0F0F]">{drill3PDspBrand}</h3>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{camps.length} campaigns</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                            <colgroup>
                              <col style={{ width: '320px' }} />
                              <col style={{ width: '150px' }} />
                              <col style={{ width: '140px' }} />
                              <col style={{ width: '80px' }} />
                              <col style={{ width: '90px' }} />
                            </colgroup>
                            <thead>
                              <tr className="border-b border-gray-200 bg-gray-50/80">
                                <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Campaign Name</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Spends (Incl. GST)</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Revenue</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ROI</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ACOS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {camps.map(c => (
                                <tr key={c.campaign_name} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                                  <td className="py-2.5 px-4 font-medium text-[#0F0F0F] truncate" title={c.campaign_name}>{c.campaign_name}</td>
                                  <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(c.ad_spend_gst, 'currency')}</td>
                                  <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(c.total_revenue, 'currency')}</td>
                                  <td className="text-right py-2.5 px-3 font-medium tabular-nums whitespace-nowrap">
                                    <span className={c.roi >= 1 ? 'text-green-600' : c.roi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{c.roi.toFixed(2)}x</span>
                                  </td>
                                  <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{c.acos.toFixed(2)}%</td>
                                </tr>
                              ))}
                              {camps.length > 1 && (
                                <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                                  <td className="text-left py-2.5 px-4 text-[#0F0F0F]">Total</td>
                                  <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(cGst, 'currency')}</td>
                                  <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(cRev, 'currency')}</td>
                                  <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap"><span className={cRoi >= 1 ? 'text-green-600' : cRoi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{cRoi.toFixed(2)}x</span></td>
                                  <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{cAcos.toFixed(2)}%</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3" data-testid="dsp-brand-drill">
                    <button onClick={() => setDrillProduct(null)} className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm transition-colors">
                      <ArrowLeft className="w-4 h-4" /> Back to summary
                    </button>
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                        <h3 className="text-sm font-semibold text-[#0F0F0F]">3P DSP</h3>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{brandRows.length} brands</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={{ width: '200px' }} />
                            <col style={{ width: '150px' }} />
                            <col style={{ width: '140px' }} />
                            <col style={{ width: '80px' }} />
                            <col style={{ width: '90px' }} />
                            <col style={{ width: '24px' }} />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/80">
                              <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Brand</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Spends (Incl. GST)</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Revenue</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ROI</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ACOS</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {brandRows.sort((a, b) => b.ad_spend - a.ad_spend).map(b => (
                              <tr key={b.brand}
                                className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors cursor-pointer"
                                onClick={() => setDrill3PDspBrand(b.brand)}
                                data-testid={`dsp-brand-${b.brand.replace(/\s+/g, '-').toLowerCase()}`}>
                                <td className="py-2.5 px-4 font-medium text-[#0F0F0F]">
                                  <div className="flex items-center gap-1.5"><span>{b.brand}</span><ChevronRight className="w-3.5 h-3.5 text-gray-400" /></div>
                                </td>
                                <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(b.ad_spend_gst, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(b.total_revenue, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 font-medium tabular-nums whitespace-nowrap">
                                  <span className={b.roi >= 1 ? 'text-green-600' : b.roi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{b.roi.toFixed(2)}x</span>
                                </td>
                                <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{b.acos.toFixed(2)}%</td>
                                <td className="py-2.5 pr-2"><ChevronRight className="w-3.5 h-3.5 text-gray-300" /></td>
                              </tr>
                            ))}
                            {brandRows.length > 1 && (
                              <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                                <td className="text-left py-2.5 px-4 text-[#0F0F0F]">Total</td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(tGst, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(tRev, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap"><span className={tRoi >= 1 ? 'text-green-600' : tRoi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{tRoi.toFixed(2)}x</span></td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{tAcos.toFixed(2)}%</td>
                                <td></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}


              {/* ===== AMAZON ONO DSP CASCADING DRILL (Brand → Campaign/Line-item) =====
                  Mirrors the 3P DSP pattern (see block above): first surface every
                  brand aggregated from the uploaded rows (brand derived from the
                  Insertion Order string), then let the user drill into a brand to
                  view the raw campaigns / line-items from the source report. */}
              {isUnlocked && drillProduct === 'ONO DSP' && (() => {
                const rawRows = filteredAdData['ONO DSP'] || [];
                const brandAgg = {};
                for (const r of rawRows) {
                  const b = r.brand || 'Other';
                  if (!brandAgg[b]) brandAgg[b] = { brand: b, ad_spend: 0, total_revenue: 0 };
                  brandAgg[b].ad_spend += Number(r.ad_spend || 0);
                  brandAgg[b].total_revenue += Number(r.total_revenue || 0);
                }
                const brandRows = Object.values(brandAgg).map(b => {
                  const gst = b.ad_spend * 1.18;
                  return {
                    ...b,
                    ad_spend_gst: gst,
                    roi: gst > 0 && b.total_revenue > 0 ? b.total_revenue / gst : 0,
                    acos: b.total_revenue > 0 ? (gst / b.total_revenue) * 100 : 0,
                  };
                });
                const tSpend = brandRows.reduce((s, r) => s + r.ad_spend, 0);
                const tGst = tSpend * 1.18;
                const tRev = brandRows.reduce((s, r) => s + r.total_revenue, 0);
                const tRoi = tGst > 0 && tRev > 0 ? tRev / tGst : 0;
                const tAcos = tRev > 0 ? (tGst / tRev) * 100 : 0;

                if (drillOnoDspBrand) {
                  const campMap = {};
                  for (const r of rawRows) {
                    if ((r.brand || 'Other') !== drillOnoDspBrand) continue;
                    const k = r.campaign_name || 'Unknown';
                    if (!campMap[k]) campMap[k] = { campaign_name: k, ad_spend: 0, total_revenue: 0 };
                    campMap[k].ad_spend += Number(r.ad_spend || 0);
                    campMap[k].total_revenue += Number(r.total_revenue || 0);
                  }
                  const camps = Object.values(campMap).map(c => {
                    const gst = c.ad_spend * 1.18;
                    return {
                      ...c,
                      ad_spend_gst: gst,
                      roi: gst > 0 && c.total_revenue > 0 ? c.total_revenue / gst : 0,
                      acos: c.total_revenue > 0 ? (gst / c.total_revenue) * 100 : 0,
                    };
                  }).sort((a, b) => b.ad_spend - a.ad_spend);
                  const cSpend = camps.reduce((s, r) => s + r.ad_spend, 0);
                  const cGst = cSpend * 1.18;
                  const cRev = camps.reduce((s, r) => s + r.total_revenue, 0);
                  const cRoi = cGst > 0 && cRev > 0 ? cRev / cGst : 0;
                  const cAcos = cRev > 0 ? (cGst / cRev) * 100 : 0;
                  return (
                    <div className="space-y-3" data-testid="ono-dsp-campaign-drill">
                      <button onClick={() => setDrillOnoDspBrand(null)} className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Back to ONO DSP brands
                      </button>
                      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                          <h3 className="text-sm font-semibold text-[#0F0F0F]">{drillOnoDspBrand}</h3>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{camps.length} campaigns</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                            <colgroup>
                              <col style={{ width: '320px' }} />
                              <col style={{ width: '150px' }} />
                              <col style={{ width: '140px' }} />
                              <col style={{ width: '80px' }} />
                              <col style={{ width: '90px' }} />
                            </colgroup>
                            <thead>
                              <tr className="border-b border-gray-200 bg-gray-50/80">
                                <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Campaign Name</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Spends (Incl. GST)</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Revenue</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ROI</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ACOS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {camps.map(c => (
                                <tr key={c.campaign_name} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                                  <td className="py-2.5 px-4 font-medium text-[#0F0F0F] truncate" title={c.campaign_name}>{c.campaign_name}</td>
                                  <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(c.ad_spend_gst, 'currency')}</td>
                                  <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(c.total_revenue, 'currency')}</td>
                                  <td className="text-right py-2.5 px-3 font-medium tabular-nums whitespace-nowrap">
                                    <span className={c.roi >= 1 ? 'text-green-600' : c.roi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{c.roi.toFixed(2)}x</span>
                                  </td>
                                  <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{c.acos.toFixed(2)}%</td>
                                </tr>
                              ))}
                              {camps.length > 1 && (
                                <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                                  <td className="text-left py-2.5 px-4 text-[#0F0F0F]">Total</td>
                                  <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(cGst, 'currency')}</td>
                                  <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(cRev, 'currency')}</td>
                                  <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap"><span className={cRoi >= 1 ? 'text-green-600' : cRoi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{cRoi.toFixed(2)}x</span></td>
                                  <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{cAcos.toFixed(2)}%</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3" data-testid="ono-dsp-brand-drill">
                    <button onClick={() => setDrillProduct(null)} className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm transition-colors">
                      <ArrowLeft className="w-4 h-4" /> Back to summary
                    </button>
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                        <h3 className="text-sm font-semibold text-[#0F0F0F]">ONO DSP</h3>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{brandRows.length} brands</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={{ width: '200px' }} />
                            <col style={{ width: '150px' }} />
                            <col style={{ width: '140px' }} />
                            <col style={{ width: '80px' }} />
                            <col style={{ width: '90px' }} />
                            <col style={{ width: '24px' }} />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/80">
                              <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Brand</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Spends (Incl. GST)</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Revenue</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ROI</th>
                              <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">ACOS</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {brandRows.sort((a, b) => b.ad_spend - a.ad_spend).map(b => (
                              <tr key={b.brand}
                                className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors cursor-pointer"
                                onClick={() => setDrillOnoDspBrand(b.brand)}
                                data-testid={`ono-dsp-brand-${b.brand.replace(/\s+/g, '-').toLowerCase()}`}>
                                <td className="py-2.5 px-4 font-medium text-[#0F0F0F]">
                                  <div className="flex items-center gap-1.5"><span>{b.brand}</span><ChevronRight className="w-3.5 h-3.5 text-gray-400" /></div>
                                </td>
                                <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(b.ad_spend_gst, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(b.total_revenue, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 font-medium tabular-nums whitespace-nowrap">
                                  <span className={b.roi >= 1 ? 'text-green-600' : b.roi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{b.roi.toFixed(2)}x</span>
                                </td>
                                <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{b.acos.toFixed(2)}%</td>
                                <td className="py-2.5 pr-2"><ChevronRight className="w-3.5 h-3.5 text-gray-300" /></td>
                              </tr>
                            ))}
                            {brandRows.length > 1 && (
                              <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                                <td className="text-left py-2.5 px-4 text-[#0F0F0F]">Total</td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(tGst, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(tRev, 'currency')}</td>
                                <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap"><span className={tRoi >= 1 ? 'text-green-600' : tRoi > 0 ? 'text-yellow-600' : 'text-gray-400'}>{tRoi.toFixed(2)}x</span></td>
                                <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{tAcos.toFixed(2)}%</td>
                                <td></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}


              {/* ===== CAMPAIGN DRILL-DOWN TABLE (non-Telegram / non-Google / non-3PDSP / non-ONO-DSP) ===== */}
              {isUnlocked && drillProduct && drillProduct !== 'Telegram Ads' && drillProduct !== 'Google Ads' && drillProduct !== '3P DSP' && drillProduct !== 'ONO DSP' && (() => {
                // Channel-scoped column override first (Zepto SP/SB use a
                // different metric set from Amazon's), then product fallback.
                const _ch = (headerFilters.channel || '').toLowerCase();
                const cols = (CAMPAIGN_COLS_BY_CHANNEL[_ch] || {})[drillProduct]
                  || CAMPAIGN_COLS[drillProduct]
                  || CAMPAIGN_COLS['PLA'];
                const rawRows = filteredAdData[drillProduct] || [];
                const aggregated = aggregateRows(rawRows);
                const sorted = campaignSort.key
                  ? (() => {
                      const arr = [...aggregated].sort((a, b) => _sortCmp(a[campaignSort.key], b[campaignSort.key]));
                      return campaignSort.dir === 'desc' ? arr.reverse() : arr;
                    })()
                  : aggregated;
                const tSpend = sorted.reduce((s, r) => s + (r.ad_spend || 0), 0);
                const tRev = sorted.reduce((s, r) => s + (r.total_revenue || 0), 0);

                // Compute metrics for this specific product
                const drillMetrics = computeMetrics({ [drillProduct]: filteredAdData[drillProduct] || [] });
                const drillPrev = Object.keys(prevAdData).length > 0 ? computeMetrics({ [drillProduct]: prevAdData[drillProduct] || [] }) : null;

                return (
                  <div className="space-y-3">
                    <button onClick={() => setDrillProduct(null)} className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm transition-colors">
                      <ArrowLeft className="w-4 h-4" /> Back to summary
                    </button>
                    <MetricCards data={drillMetrics} prev={drillPrev} />
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                        <h3 className="text-sm font-semibold text-[#0F0F0F]">{drillProduct}</h3>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{sorted.length} campaigns</span>
                      </div>
                      <div className="overflow-x-auto">
                        {(() => {
                          // Width per column type — locked to prevent layout shift on data refresh
                          const colWidth = (col) => {
                            if (col.key === 'campaign_name') return 240;
                            if (col.key === 'ad_group') return 200;
                            if (col.align === 'left') return 160;
                            if (col.fmt === 'currency' || col.fmt === 'currency_raw') return 140;
                            if (col.fmt === 'pct') return 90;
                            if (col.fmt === 'decimal') return 90;
                            return 110;
                          };
                          const tableWidth = cols.reduce((s, c) => s + colWidth(c), 0);
                          return (
                            <table className="text-sm" style={{ tableLayout: 'fixed', width: tableWidth }} data-testid="ad-campaign-table">
                              <colgroup>
                                {cols.map(col => <col key={col.key} style={{ width: `${colWidth(col)}px` }} />)}
                              </colgroup>
                              <thead>
                                <tr className="border-b border-gray-200 bg-gray-50/80">
                                  {cols.map(col => (
                                    <th key={col.key}
                                      className={`${col.align === 'left' ? 'text-left' : 'text-right'} py-2.5 px-3 font-semibold text-gray-500 text-[11px] uppercase tracking-wider whitespace-nowrap align-middle cursor-pointer hover:text-gray-700 select-none`}
                                      onClick={() => toggleSort(col.key, setCampaignSort, campaignSort)}>
                                      <span className={`inline-flex items-center gap-1.5 ${col.align === 'left' ? 'justify-start' : 'justify-end'}`}>
                                        <span>{col.label}</span><SortIcon colKey={col.key} config={campaignSort} />
                                      </span>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {sorted.slice(0, 100).map((row, idx) => (
                                  <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors">
                                    {cols.map(col => {
                                      // Support dotted-path keys (e.g. "extra.status")
                                      // so Zepto-style columns can read from the row's
                                      // `extra` metadata blob without flattening it.
                                      const cellVal = _readKeyPath(row, col.key);
                                      return (
                                        <td key={col.key}
                                          className={`${col.align === 'left' ? 'text-left' : 'text-right'} py-2 px-3 text-gray-700 tabular-nums whitespace-nowrap ${col.key === 'campaign_name' || col.key === 'ad_group' ? 'font-medium text-[#0F0F0F] truncate' : ''}`}
                                          title={col.key === 'campaign_name' || col.key === 'ad_group' ? cellVal : undefined}>
                                          {col.align === 'left' ? (cellVal || '-') : fmtVal(cellVal, col.fmt)}
                                      </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                                {sorted.length > 100 && (
                                  <tr><td colSpan={cols.length} className="py-2 text-center text-xs text-gray-400">Showing top 100 of {sorted.length} campaigns</td></tr>
                                )}
                                {/* Total Row */}
                                <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                                  <td className="text-left py-2.5 px-3 text-[#0F0F0F] whitespace-nowrap truncate">Total ({sorted.length})</td>
                                  {cols.slice(1).map(col => {
                                    if (col.align === 'left') return <td key={col.key} className="py-2.5 px-3 text-gray-400">-</td>;
                                    const isRate = (col.fmt || '').endsWith('_raw') && col.fmt !== 'currency_raw' && col.fmt !== 'number';
                                    // Ratio / cap fields that aren't summable — show weighted aggregate
                                    // or a dash, never a numeric sum (a sum of ROAS values is nonsense).
                                    const lowerKey = (col.key || '').toLowerCase();
                                    const isRatioKey = ['cpc','cpm','roas','roas_native','robas','daily_budget'].includes(col.key) || lowerKey.includes('budget') || lowerKey === 'roas_native';
                                    if (col.key === 'ad_spend_gst') {
                                      return <td key={col.key} className="text-right py-2.5 px-3 text-gray-600 tabular-nums whitespace-nowrap">{fmtVal(tSpend * 1.18, 'currency')}</td>;
                                    }
                                    if (col.key === 'ctr') return <td key={col.key} className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap">{fmtVal(sorted.reduce((s,r)=>s+(r.views||0),0) > 0 ? (sorted.reduce((s,r)=>s+(r.clicks||0),0) / sorted.reduce((s,r)=>s+(r.views||0),0)) * 100 : 0, 'pct')}</td>;
                                    if (col.key === 'cvr') return <td key={col.key} className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap">{fmtVal(sorted.reduce((s,r)=>s+(r.clicks||0),0) > 0 ? (sorted.reduce((s,r)=>s+(r.total_converted_units||0),0) / sorted.reduce((s,r)=>s+(r.clicks||0),0)) * 100 : 0, 'pct')}</td>;
                                    if (col.key === 'roi') return <td key={col.key} className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap">{fmtVal(tSpend > 0 ? tRev / (tSpend * 1.18) : 0, 'decimal')}</td>;
                                    if (col.key === 'acos') return <td key={col.key} className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap">{fmtVal(tRev > 0 ? ((tSpend * 1.18) / tRev) * 100 : 0, 'pct')}</td>;
                                    // Weighted aggregate ROAS/ROBAS = total_revenue / total_spend (preserves pre-GST native convention).
                                    if (col.key === 'roas_native' || col.key === 'robas') {
                                      return <td key={col.key} className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap">{fmtVal(tSpend > 0 ? tRev / tSpend : 0, 'decimal')}</td>;
                                    }
                                    // Weighted CPC / CPM
                                    if (col.key === 'cpc') {
                                      const totalClicks = sorted.reduce((s,r)=>s+(r.clicks||0),0);
                                      return <td key={col.key} className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap">{fmtVal(totalClicks > 0 ? tSpend / totalClicks : 0, 'currency')}</td>;
                                    }
                                    if (col.key === 'cpm') {
                                      const totalViews = sorted.reduce((s,r)=>s+(r.views||0),0);
                                      return <td key={col.key} className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap">{fmtVal(totalViews > 0 ? (tSpend * 1000) / totalViews : 0, 'currency')}</td>;
                                    }
                                    if (isRatioKey || isRate) return <td key={col.key} className="text-right py-2.5 px-3 text-gray-400">-</td>;
                                    const total = sorted.reduce((s, r) => s + (Number(r[col.key]) || 0), 0);
                                    if (total === 0) return <td key={col.key} className="text-right py-2.5 px-3 text-gray-400">-</td>;
                                    return <td key={col.key} className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(total, col.fmt)}</td>;
                                  })}
                                </tr>
                              </tbody>
                            </table>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
            )}
          </div>
        </main>
      </div>


      {/* Add/Remove Metrics Picker */}
      {showMetricPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { setShowMetricPicker(false); setShowCustomForm(false); }} data-testid="metric-picker-overlay">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="metric-picker">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-base font-bold text-[#0F0F0F]">Add / Remove Metrics</h2>
              <button onClick={() => { setShowMetricPicker(false); setShowCustomForm(false); }} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="p-4 space-y-1 overflow-y-auto flex-1">
              {[...ALL_METRICS, ...customMetrics].map(m => {
                const active = visibleMetrics.includes(m.key);
                return (
                  <button key={m.key} onClick={() => {
                    setVisibleMetrics(prev => active ? prev.filter(k => k !== m.key) : [...prev, m.key]);
                  }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${active ? 'bg-[#32B4AC]/10 border border-[#32B4AC]/30' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}
                    data-testid={`metric-toggle-${m.key}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${active ? 'text-[#0F0F0F]' : 'text-gray-500'}`}>{m.label}</span>
                      {m.formula && <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">custom</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {m.formula && (
                        <button onClick={(e) => { e.stopPropagation(); setCustomMetrics(prev => prev.filter(c => c.key !== m.key)); setVisibleMetrics(prev => prev.filter(k => k !== m.key)); }}
                          className="text-red-400 hover:text-red-600"><Minus className="w-3.5 h-3.5" /></button>
                      )}
                      {active ? <Minus className="w-4 h-4 text-red-400" /> : <Plus className="w-4 h-4 text-[#32B4AC]" />}
                    </div>
                  </button>
                );
              })}

              {/* Custom metric form */}
              {!showCustomForm ? (
                <button onClick={() => setShowCustomForm(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 text-sm mt-2"
                  data-testid="create-custom-metric-btn">
                  <Plus className="w-4 h-4" /> Create Custom Metric
                </button>
              ) : (
                <div className="mt-2 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
                  <div className="text-sm font-semibold text-[#0F0F0F]">New Custom Metric</div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Metric Name</label>
                    <input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="e.g. Cost Per Unit"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#32B4AC]" data-testid="custom-metric-name" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Formula / Function</label>
                    <input value={customFormula} onChange={(e) => setCustomFormula(e.target.value)} placeholder="e.g. spend / units"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-[#32B4AC]" data-testid="custom-metric-formula" />
                    <p className="text-[10px] text-gray-400 mt-1">Variables: spend, revenue, views, clicks, units, ctr, cvr, roi, acos</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowCustomForm(false)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-100">Cancel</button>
                    <button onClick={() => {
                      if (!customName.trim() || !customFormula.trim()) return;
                      const key = `custom_${Date.now()}`;
                      setCustomMetrics(prev => [...prev, { key, label: customName.trim(), formula: customFormula.trim(), fmt: 'number' }]);
                      setVisibleMetrics(prev => [...prev, key]);
                      setCustomName(''); setCustomFormula(''); setShowCustomForm(false);
                    }} className="px-3 py-1.5 text-sm bg-[#32B4AC] text-white rounded-lg hover:bg-[#2a9d95]" data-testid="save-custom-metric-btn">
                      Add Metric
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end shrink-0">
              <button onClick={() => { setShowMetricPicker(false); setShowCustomForm(false); }} className="px-4 py-2 bg-[#32B4AC] text-white text-sm rounded-lg hover:bg-[#2a9d95]">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewProduct && AD_PREVIEWS[previewProduct] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setPreviewProduct(null)} data-testid="preview-modal-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-[#0F0F0F]">{AD_PREVIEWS[previewProduct].label}</h2>
                <p className="text-sm text-gray-500 mt-0.5">Ad placement previews on {selectedChannel || 'this channel'}</p>
              </div>
              <button onClick={() => setPreviewProduct(null)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="preview-modal-close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            {/* Modal Body */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 space-y-6">
              {AD_PREVIEWS[previewProduct].placements.map((placement, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#32B4AC]/10 text-[#32B4AC] text-xs font-bold">{idx + 1}</span>
                    <h3 className="text-sm font-semibold text-[#0F0F0F]">{placement.name}</h3>
                  </div>
                  <p className="text-xs text-gray-500 ml-8">{placement.description}</p>
                  <div className="ml-8 rounded-xl border border-gray-200 overflow-hidden bg-gray-50 shadow-sm">
                    <img
                      src={placement.image}
                      alt={placement.name}
                      className="w-full h-auto object-contain max-h-[400px]"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
